// REACT IMPORTS
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _debounce from "lodash/debounce";
import moment from "moment";
import { unwrapResult } from "@reduxjs/toolkit";
import Drawer from "components/ui-component/SideDrawer";
// COMPONENT IMPORTS
import TabSwitch from "components/ui-component/TabSwitch";
import {
  addNotification,
  customerDetailGet,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";

// ICON IMPORTS
import logo from "icons/smallLogo.svg";
import { EditIcon } from "icons/payments/EditIcon";
import OneTimePayment from "components/makePayment/OneTimePayment";
import OutsideClickHandler from "components/ui-component/OutsideClickHandler";
import {
  getPaymentNewPlan,
  getPaymentPlanDates,
  useLeadData,
  usePaymentNewPlanData,
  usePortalSettingsData,
  getHolidayList,
} from "store/slices/dashboardSlice";
import {
  addCard,
  useBankData,
  useCardData,
  useIsDataLoading,
  useOldWalletData,
} from "store/slices/paymentMethodSlice";
import SchedulePayment from "components/makePayment/SchedulePayment";
import {
  CalculateCustomAmount,
  CalculateCustomPercentage,
  calculateOutstanding,
} from "utils/amount/amountCalculate";
import {
  cardToShortname,
  checkOverPaymentAmount,
  formatCurrency,
  getBrowserTimezoneName,
  getBrowserTimezoneOffset,
  getGeneralDetailMarkers,
  getMethod,
  getOperatingSystem,
  numberOfTransactions,
  percentageCount,
} from "utils/HelperFunctions";
import AvailableOffer from "components/makePayment/AvailableOffer";
import { OfferIcon } from "icons/payments/OfferIcon";
import { InfoIcon } from "utils/icon";
import {
  createPaypalPaymentToken,
  createPaypalToken,
  duplicatePaymentGet,
  getpaymentPermission,
  makeGooglePay,
  makePayment,
  paymentDownload,
  paymentLogoGet,
  paymentOfferGet,
  payPalOneTimeCreate,
  payPalOneTimeExecute,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  storePaymnetPlan,
  storePayPalOrder,
  verifyWalletPay,
  wirePaymentProcess,
  setIsPaymentProcessing,
  sendPaymentAssistanceRequest,
  generalSettingsGet,
} from "store/slices/paymentsSlice";
import { applePayDebug } from "utils/applePayDebug";
import { BASE_URL, VERIFY_WALLET_PAYMENT } from "store/api/constApi";
import { useAppDispatch, useAppSelector } from "store/hooks";
import PaymentLinkProcessFailed from "components/actionCenter/PaymentLinkProcessFailed";
import {
  Payload,
  PaymentOffer,
  PaymentOfferResponse,
  resolveOfferLinkage,
} from "./paymentTypes";
import Button from "components/form/Button";
import PlanPreview from "components/dashboard/PlanPreview";
import { PaypalIcon } from "icons/dashboard/paypal";
import { CashAppIcon } from "icons/dashboard/cashAppIcon";
import OneTimeSuccess from "components/makePayment/OneTimeSuccess";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PaymentScheduledSuccess from "components/actionCenter/PaymentScheduledSuccess";
import PaymentScheduledFailed from "components/actionCenter/PaymentScheduledFailed";
import { CardIcon } from "icons/payments/Card";
import { BankIconSm } from "icons/payments/BankIconSm";
import WireTransferIcon from "icons/payments/WireTransferIcon";
import WirePaymentSuccess from "components/actionCenter/WirePaymentSuccess";
import Footer from "components/layout/footer";
import BackArrow from "icons/subscribe/BackArrow";
import CustomTooltip from "components/ui-component/CustomTooltip";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  updateCheckAmountState,
  useIsFinalDisclosureLoading,
  useIsLogin,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { formatPaymentListDate } from "utils/DateFormat";
import CustomAmountInput from "components/ui-component/CustomAmountInput";
import DisclosureInitial from "components/makePayment/DisclosureInitial";
import AmazonPay from "icons/dashboard/AmazonPay";
import { decode } from "utils/HelperFunctions";
import { useMediaQuery } from "react-responsive";
import { valuesProps } from "types/paymentMethods";
import { initialValues } from "utils/countryAddressList";
import {
  PaymentGateway,
  PaymentMethodList,
  SettingsValue,
} from "types/payments";
import { RootState } from "store/store";
import CustomModal from "components/ui-component/CustomModal";
import { useNavigationGuard } from "hooks/useNavigationGuard";
import AssistanceRequest from "components/makePayment/AssistanceRequest";
import {
  calculateScheduleSummary,
  getScheduleEndDateError,
  mergeGeneratedCustomSchedule,
} from "utils/updatePaymentPlanUtils";
import {
  getNonBudgetOfferTransactionLimitError,
  getSelectedOfferEndDateLimitError,
} from "utils/selectedOfferLimits";
import {
  buildPayPalOneTimeReturnUrl,
  resolvePaymentCallbackFlow,
} from "./paymentCallbackUtils";
import {
  resolvePaymentAmountLimits,
  validateScheduleAmounts,
} from "utils/paymentAmountValidation";

type Transaction = {
  billing_date?: string;
  payment_date?: string;
  amount?: number | string;
};

type PaymentPlan = {
  transactions: Transaction[];
  grand_total?: number | string;
  amount_subtotal?: number | string;
  percentage_amounts?: number[];
};

type Data = {
  paymentPlan: PaymentPlan;
  balance?: number;
};

type HandleChangeType = "date" | "amount" | "percent";
export type HandleTabChangeType = "one_time" | "schedule";

interface AddNewCardProps {
  cc_token: string;
  cc_token2: string;
  lead_id: number;
  account_id: string;
  accounts_id: string;
  gateway_id: number;
  gateway_name: string;
  clusterid: number;
  cc_expiry: string;
  cc_4digit: string;
  cc_cvv: string;
  default: string;
  cc_type: string;
  payment_method: string;
  card_funding_type: string;
  account_number: string;
  routing_number: string;
  swift_code: string;
  bank_name: string;
  billing_address: string;
  billing_address2: string;
  billing_city: string;
  billing_state: string;
  billing_zip: string;
  billing_country: string;
  account_name: string;
  id: number;
}

const MakePayment = () => {
  const userData = useUserData();
  const dispatch = useAppDispatch();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const accountData = useMemo(() => {
    return userData?.data?.accountData;
  }, [userData]);

  const { paymentLogo, generalSettings, checkedDuplicatePayment } =
    useAppSelector((state: RootState) => state.payment);
  const { checkAmount } = useAppSelector((state: RootState) => state.common);
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  const drawerRef = useRef<HTMLSpanElement>(null);
  const paymentContentRef = useRef<HTMLDivElement>(null);
  const { brandColor, isDark } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const { holidayList } = useAppSelector((state: RootState) => state.dashboard);
  const showFinalDisclosure = useShowFinalDisclosure();
  const isFinalDisclousreLoading = useIsFinalDisclosureLoading();
  const [scheduleMethod, setScheduleMethod] = useState<string>("CC");

  const location = useLocation();
  const portalSettings = usePortalSettingsData();
  const searchParams = new URLSearchParams(location.search);
  const isLogin = useIsLogin();
  const defaultPaymentMethodList = [
    {
      name: "Card",
      icon: <CardIcon />,
    },
    {
      name: "Bank",
      icon: <BankIconSm />,
    },
  ];

  const [values, setValues] = useState<valuesProps>(initialValues);
  const [selectedMethod, setSelectedMethod] = useState<
    "Card" | "Bank" | "PayPal" | "Cash App Pay" | string
  >("Card");
  const amtInputContainerRef = useRef<any>();
  const accountLeadData = useLeadData();
  const isDataLoading = useIsDataLoading();
  const [tab, setTab] = useState<HandleTabChangeType>("one_time");
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editVal, setEditVal] = useState<string>("0.00");
  const valueRef = useRef<string>(editVal);
  valueRef.current = editVal;

  const [updatedArr, setUpdatedArr] = useState<any>({
    updatedValue: [],
    createdValue: [],
    deletedValue: [],
  });
  const paymentPlanDataRedux = usePaymentNewPlanData();
  const getOffDaysPayments = useCallback(() => {
    return (
      generalSettings?.off_days_payments ??
      generalSettings?.systemSettings?.off_days_payments
    );
  }, [generalSettings]);
  const checkStartDate = useCallback(
    (date: string, holidays: any[] = holidayList) => {
      const currentDate = moment(
        date,
        ["MM/DD/YYYY", "YYYY-MM-DD", moment.ISO_8601],
        true
      );
      const formatted = currentDate.isValid()
        ? currentDate.format("YYYY-MM-DD")
        : moment(date).format("YYYY-MM-DD");
      const isHoliday = holidays?.some((holiday: any) => {
        const holidayDate = moment(
          holiday?.date ?? holiday,
          ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601],
          true
        );
        return (
          holidayDate.isValid() &&
          holidayDate.format("YYYY-MM-DD") === formatted
        );
      });

      if (isHoliday) return false;

      const day = currentDate.isValid()
        ? currentDate.day()
        : moment(date).day();
      return [1, 2, 3, 4, 5].includes(day);
    },
    [holidayList]
  );
  const getStartDate = useCallback(
    (given?: string | Date | moment.Moment, holidays: any[] = holidayList) => {
      const baseDate = given ? moment(given) : moment();
      const dateObj = baseDate.isValid() ? baseDate.clone() : moment();

      while (
        getOffDaysPayments() !== "Y" &&
        !checkStartDate(dateObj.format("MM/DD/YYYY"), holidays)
      ) {
        dateObj.add(1, "day");
      }

      return dateObj.format("MM/DD/YYYY");
    },
    [checkStartDate, getOffDaysPayments, holidayList]
  );
  const initialState = {
    filterParamValue: "",
    filterParam: "search",
    page: 1,
    state: "AL",
    country: "US",
    isrecurring: "oneTime",
    agreementType: "new",
    type: "Amount",
    amount: "0.00",
    new_offer_type: "Amount",
    paymentPlan: { start_date: getStartDate() },
    selectedCard: {},
    selectedBank: {},
  };
  const [dataPayment, setDataPayment] = useState<any>(initialState);
  const [completedSteps, setCompletedSteps] = useState<any>([0]);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [failedReason, setFailedReason] = useState<any>(null);
  const [data, setData] = useState<any>({
    lead_id: accountLeadData?.lead_id,
    update_lead: "Y",
    clusterid: clusterId,
    p_accound_id: "454",
    first_name: userData?.data?.accountData?.first_name,
    last_name: userData?.data?.accountData?.last_name,
    lead_owner: "",

    paymentPlan: {
      amount: 0,
      transactions: [],
      amount_subtotal: 0,
      grand_total: 0,
      payment_schedule: "",
      no_of_transaction: 5,
      transaction_type: "amount",
      start_date: getStartDate(),
      schedule_amount: "fixed",
      schedule_fld: "daily",
    },
  });
  const [isStartDate, setIsStartDate] = useState<boolean>(true);
  const [availableOffer, setAvailableOffer] = useState<boolean>(true);
  const [availableOfferData, setAvailableOfferData] = useState<PaymentOffer[]>(
    []
  );
  const [offerApiCalled, setOfferApiCaalled] = useState<boolean>(false);
  const { isPaymentProcessing } = useAppSelector(
    (state: RootState) => state.payment
  );

  const [initialDisclosureAccept, setInitialDisclosureAccept] =
    useState<boolean>(false);

  const scrollPaymentContentToTop = (): void => {
    const scrollToTop = () => {
      let parent = paymentContentRef.current?.parentElement;

      while (parent) {
        if (parent.scrollHeight > parent.clientHeight) {
          parent.scrollTo({ top: 0, behavior: "auto" });
        }
        parent = parent.parentElement;
      }

      paymentContentRef.current?.scrollTo({ top: 0, behavior: "auto" });
      document.scrollingElement?.scrollTo({ top: 0, behavior: "auto" });
      window.scrollTo({ top: 0, behavior: "auto" });
    };

    scrollToTop();
    requestAnimationFrame(scrollToTop);
    window.setTimeout(scrollToTop, 100);
  };
  const [isAddNewCard, setIsAddNewCard] = useState<boolean>(false);
  const [selectedOffer, setSelectedOffer] = useState<PaymentOffer>();
  const [availableStateLoading, setAvailableStateLoading] =
    useState<boolean>(false);
  const [paymentProcess, setPaymentProcess] = useState<any>(null);
  const [hideButtons, setHideButtons] = useState<boolean>(false);
  const [email, setEmail] = useState("");
  const [invoiceId, setInvoiceId] = useState<any>("");
  const [scheduleId, setScheduleId] = useState<any>("");
  const [orderId, setOrderId] = useState<any>("");
  const [amount, setAmount] = useState("");
  const [isRealTime, setIsRealTime] = useState<boolean>(false);
  const [activeOffer, setActiveOffer] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isApiCall, setIsApiCall] = useState<boolean>(false);
  const latestScheduleRequestRef = useRef(0);
  const walletVerificationInFlightRef = useRef<Set<string>>(new Set());
  const [scheduleRequestId, setScheduleRequestId] = useState(0);
  const [isAssistanceLoading, setIsAssistanceLoading] =
    useState<boolean>(false);
  const [isAssistanceRequested, setIsAssistanceRequested] =
    useState<boolean>(false);

  const [isEndDateError, setIsEndDateError] = useState<boolean>(false);
  const [dateErrMsg, setDateErrMsg] = useState<string | null>(null);

  const [scheduleCreateLoading, setScheduleCreateLoading] =
    useState<boolean>(false);
  const [savedCardsEmail, setSavedCardsEmail] = useState<string>("");
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [paymentDetailsMain, setPaymentDetailsMain] = useState<any>();
  const [budgerCalculatorIncome, setBudgetCalculatorIncome] =
    useState<number>(0);
  const [offerAmount, setOfferAmount] = useState<number>(0);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false);
  const [paymentMethodList, setPaymentMethodList] =
    useState<PaymentMethodList[]>();
  const { paymentPermission } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [isWirePayment, setIsWirePayment] = useState<boolean>(false);
  const [isConfirmButtonDisable, setIsConfirmButtonDisable] =
    useState<boolean>(true);
  const [isCustomPlan, setIsCustomPlan] = useState<boolean>(false);
  const paymentPlanBeforeAddRef = useRef<any>(null);
  const [offerStep, setOfferStep] = useState(1);
  const selectedOfferTransactionError = useMemo(
    () =>
      getNonBudgetOfferTransactionLimitError(
        selectedOffer,
        portalSettings?.transaction_limit
      ),
    [selectedOffer, portalSettings?.transaction_limit]
  );

  const getCurrentSelectedOfferEndDateError = useCallback(
    (transactions: any[] = data?.paymentPlan?.transactions || []) =>
      getSelectedOfferEndDateLimitError({
        offer: selectedOffer,
        transactions,
        configuredLimit: portalSettings?.end_date_limit_external,
      }),
    [selectedOffer, portalSettings?.end_date_limit_external, data]
  );

  const [wireDetails, setWireDetails] = useState({
    email: "",
    bank: "",
    routing: "",
    account: "",
    swift: "",
    dueDate: "",
  });
  const [showDetails, setShowDetails] = useState<boolean>(true);
  const [isSelectStartDate, setIsSelectStartDate] = useState<boolean>(false);
  const [amtErr, setAmtErr] = useState<string | null>(null);
  const [scheduleAmountErr, setScheduleAmountErr] = useState<string | null>(
    null
  );
  const [amountLimitsStatus, setAmountLimitsStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [isManualForm, setIsManualForm] = useState<boolean>(true);
  const navigate = useNavigate();

  const requestScheduleRefresh = useCallback((shouldRefresh: boolean) => {
    if (!shouldRefresh) {
      setIsApiCall(false);
      return;
    }

    const nextRequestId = latestScheduleRequestRef.current + 1;
    latestScheduleRequestRef.current = nextRequestId;
    setIsApiCall(true);
    setScheduleRequestId(nextRequestId);
  }, []);

  const debouncedScheduleRefresh = useMemo(
    () => _debounce(() => requestScheduleRefresh(true), 1500),
    [requestScheduleRefresh]
  );
  const handleDebouncedChange = useCallback(
    (shouldRefresh = true) => {
      if (!shouldRefresh) {
        debouncedScheduleRefresh.cancel();
        latestScheduleRequestRef.current += 1;
        setScheduleRequestId(0);
        setIsApiCall(false);
        setIsLoading(false);
        return;
      }

      debouncedScheduleRefresh();
    },
    [debouncedScheduleRefresh]
  );

  useEffect(
    () => () => {
      debouncedScheduleRefresh.cancel();
    },
    [debouncedScheduleRefresh]
  );

  const { payPalOrder } = useAppSelector((state: RootState) => state.payment);
  const offerLinkage = resolveOfferLinkage(
    selectedOffer?.offer_type,
    selectedOffer?.id
  );
  const onGetLogo = async () => {
    try {
      let payload = {
        cluster_id: clusterId,
      };
      await dispatch(paymentLogoGet(payload)).unwrap();
    } catch (error: any) {
      console.error("get logo err: -->", error?.message);
    }
  };

  useEffect(() => {
    let isActive = true;
    setAmountLimitsStatus("loading");

    dispatch(generalSettingsGet({}))
      .unwrap()
      .then((response: any) => {
        if (!isActive) return;
        setAmountLimitsStatus(response?.body?.success ? "ready" : "error");
      })
      .catch(() => {
        if (isActive) setAmountLimitsStatus("error");
      });

    return () => {
      isActive = false;
    };
  }, [dispatch, clusterId]);

  useEffect(() => {
    const shouldReset = !location.search && !isPaymentProcessing;

    if (shouldReset) {
      resetMakePaymentState();
    }
  }, [location.key, location.search]);

  const addNewRow = () => {
    if (isLoading) return;

    paymentPlanBeforeAddRef.current = {
      ...data.paymentPlan,
      transactions: [...(data.paymentPlan?.transactions || [])],
    };
    setData((data: any) => ({
      ...data,
      paymentPlan: {
        ...data.paymentPlan,
        no_of_transaction: data.paymentPlan.no_of_transaction + 1,
      },
    }));
    setIsCustomPlan(true);
    setTimeout(() => {
      requestScheduleRefresh(true);
    }, 0);
  };

  const removeRow = (index: number) => {
    const tmpTransactions = Array.from(data.paymentPlan.transactions);

    tmpTransactions.splice(index, 1);
    const calculatedDataInitial = calculateOutstanding(tmpTransactions);
    setData((data: any) => ({
      ...data,
      paymentPlan: {
        ...data.paymentPlan,
        transactions: tmpTransactions,
        no_of_transaction: data.paymentPlan.no_of_transaction - 1,
        grand_total: calculatedDataInitial.totalAmount,
        amount_subtotal: calculatedDataInitial.totalAmount,
      },
    }));
    setIsCustomPlan(true);
    setTimeout(() => {
      requestScheduleRefresh(true);
    }, 0);
  };

  const handleBlur = (value: string): boolean => {
    if (isEdit) {
      const { min, max, isConfigured } = resolvePaymentAmountLimits(
        generalSettings,
        userData?.data?.accountData?.balance
      );
      if (!isConfigured) {
        setAmtErr(
          "Payment limits are unavailable. Please refresh and try again."
        );
        return true;
      }
      let str = value.replaceAll(",", "");
      if (parseFloat(str) < min || parseFloat(str) > max) {
        setAmtErr(
          `Amount should be between ${formatCurrency(min)} and ${formatCurrency(
            max
          )}`
        );
        return true;
      } else {
        setAmtErr(null);
        return false;
      }
    } else {
      return false;
    }
  };

  const handleOutsideClick = () => {
    setIsEdit(handleBlur(valueRef.current));
  };

  const insertInOrder = (array: any, newNumber: any) => {
    const dummyArr: any = JSON.parse(JSON.stringify(array));
    const index = dummyArr.findIndex((val: any) => newNumber < val);
    if (index === -1) {
      dummyArr.push(newNumber);
    } else {
      dummyArr.splice(index, 0, newNumber);
    }
    return dummyArr;
  };

  const handleChange = (type: HandleChangeType, index: number, value: any) => {
    if (type === "amount" || type === "percent") {
      setScheduleAmountErr(null);
    }
    dispatch(setIsPaymentProcessing(true));
    const tmpPaymentPlan: PaymentPlan = { ...data.paymentPlan };
    const tmpTransactionArr: Transaction[] = JSON.parse(
      JSON.stringify(tmpPaymentPlan.transactions)
    );

    const updateTransactions = (
      transactions: Transaction[],
      updates: Partial<PaymentPlan>
    ) => {
      setData((state: Data) => ({
        ...state,
        paymentPlan: {
          ...state.paymentPlan,
          transactions,
          ...updates,
        },
      }));
    };

    const handleAmountChange = (value: any) => {
      const formattedValue =
        value === "" || value === "0" ? "" : parseFloat(value).toFixed(2);
      tmpTransactionArr?.[index] &&
        (tmpTransactionArr[index].amount = formattedValue);

      if (value) {
        const calculatedData = CalculateCustomAmount(
          tmpTransactionArr,
          index,
          formattedValue,
          calculateTotal(accountData?.balance, selectedOffer)
        );
        updateTransactions(calculatedData.dataArr, {
          grand_total: calculatedData.totalAmount,
          amount_subtotal: calculatedData.totalAmount,
        });
      } else {
        updateTransactions(tmpTransactionArr, {});
      }
    };

    const handlePercentChange = (value: any) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || numericValue <= 0) return;

      const trimmed = value?.toString().trim();
      if (!tmpTransactionArr[index]) return;
      tmpTransactionArr[index].amount =
        tmpTransactionArr[index].amount !== "" &&
        trimmed !== "" &&
        Number(trimmed) !== 0
          ? Number(trimmed)
          : "";
      const totalPercentage = percentageCount(tmpTransactionArr);

      if (totalPercentage > 100) return;

      if (value) {
        const calculatedData = CalculateCustomPercentage(
          tmpTransactionArr,
          index,
          value,
          calculateTotal(accountData?.balance, selectedOffer)
        );
        updateTransactions(calculatedData.dataArr, {
          percentage_amounts: calculatedData.amountArray,
          grand_total: calculatedData.totalAmount,
          amount_subtotal: calculatedData.totalAmount,
        });
      } else {
        updateTransactions(tmpTransactionArr, {});
      }
    };

    switch (type) {
      case "date":
        tmpTransactionArr[index].billing_date = value;
        tmpTransactionArr[index].payment_date = value;
        updateTransactions(tmpTransactionArr, {});
        setIsCustomPlan(true);
        setTimeout(() => {
          requestScheduleRefresh(true);
        }, 500);
        break;
      case "amount":
        handleAmountChange(value);
        break;
      case "percent":
        handlePercentChange(value);
        break;
    }

    if (
      !updatedArr.createdValue.includes(index) &&
      !updatedArr.updatedValue.includes(index)
    ) {
      setUpdatedArr((state: any) => ({
        ...state,
        updatedValue: insertInOrder(state.updatedValue, index),
      }));
    }
  };

  const handlePrev = () => {
    const tmp = Array.from(completedSteps);
    tmp.pop(); // Remove the last completed step when going back
    setCurrentStep(currentStep - 1);
    setCompletedSteps(tmp);
  };

  function calculateTotal(balance?: number, offer?: any): number {
    if (balance === undefined || offer === undefined) return balance || 0;

    const settlementAmount = parseFloat(offer.settlement_amount as string);
    if (offer.settlement_offer === "F") {
      return balance - settlementAmount;
    } else if (offer.settlement_offer === "P") {
      return balance - balance * (settlementAmount / 100);
    } else {
      return balance;
    }
  }

  const getEffectiveOfferInstallmentAmount = (): number | null => {
    if (!selectedOffer) return null;
    if (selectedOffer.offer_type === "BC") {
      return Number(budgerCalculatorIncome);
    }

    const transactionCount = Math.max(
      1,
      Number(selectedOffer.no_of_transactions || 1)
    );
    return (
      calculateTotal(accountData?.balance, selectedOffer) / transactionCount
    );
  };

  const getCurrentScheduleAmountError = (): string | null => {
    if (amountLimitsStatus === "loading") {
      return "Payment limits are loading. Please wait.";
    }
    if (amountLimitsStatus === "error") {
      return "Payment limits are unavailable. Please refresh and try again.";
    }

    const offerTotal = selectedOffer
      ? calculateTotal(accountData?.balance, selectedOffer)
      : undefined;
    const amountLimitBalance = offerTotal ?? accountData?.balance;
    const isCustomPercentPlan =
      data?.paymentPlan?.schedule_amount === "custom" &&
      data?.paymentPlan?.transaction_type === "percent";
    const installmentAmounts = isCustomPercentPlan
      ? data?.paymentPlan?.percentage_amounts || []
      : (data?.paymentPlan?.transactions || []).map(
          (transaction: Transaction) => transaction.amount
        );
    const result = validateScheduleAmounts({
      settings: generalSettings,
      balance: amountLimitBalance,
      amounts: installmentAmounts,
      expectedTotal: selectedOffer ? offerTotal : undefined,
      expectedCount: selectedOffer
        ? selectedOffer.offer_type === "BC"
          ? data.paymentPlan.no_of_transaction
          : selectedOffer.no_of_transactions || 1
        : undefined,
    });

    switch (result.code) {
      case "LIMITS_UNAVAILABLE":
        return "Payment limits are unavailable. Please refresh and try again.";
      case "INVALID_AMOUNT":
        return "Enter a valid amount for every installment.";
      case "OUTSIDE_LIMITS":
        return `Amount should be between ${formatCurrency(
          result.limits.min
        )} and ${formatCurrency(result.limits.max)}`;
      case "OFFER_SCHEDULE_MISMATCH":
        return "The generated schedule does not match the selected offer. Please reload the offer and try again.";
      default:
        return null;
    }
  };

  const handleSave = () => {
    const amountError = getCurrentScheduleAmountError();
    setScheduleAmountErr(amountError);
    if (amountError) return;

    const selectedOfferEndDateError = getCurrentSelectedOfferEndDateError();

    if (selectedOfferEndDateError) {
      setIsEndDateError(true);
      setDateErrMsg(selectedOfferEndDateError.message);
    }

    // Schedule edits (including Custom-frequency rows) are validated
    // asynchronously. Do not allow the payment-method step until that
    // validation has completed, otherwise a user can advance with a row that
    // may be outside the configured end-date limit.
    if (
      isLoading ||
      isApiCall ||
      Boolean(selectedOfferTransactionError) ||
      Boolean(selectedOfferEndDateError) ||
      isEndDateError ||
      paymentPlanBeforeAddRef.current
    ) {
      return;
    }

    const tmp = Array.from(completedSteps);
    tmp.push(currentStep);
    setCompletedSteps(tmp);
    setCurrentStep((prevCurr) => prevCurr + 1);
  };

  const normalizeScheduleAmount = (value: any): number | undefined => {
    const numericValue = Number(String(value ?? "").replace(/[$,]/g, ""));
    return Number.isFinite(numericValue) ? numericValue : undefined;
  };

  const getScheduleResponseTotals = (
    responseData: any,
    fallbackSubtotal: number,
    fallbackTotal: number = fallbackSubtotal
  ) => {
    const amountSubtotal = normalizeScheduleAmount(
      responseData?.amount_subtotal ??
        responseData?.amount_total ??
        responseData?.subtotal
    );
    const grandTotal = normalizeScheduleAmount(
      responseData?.grand_total ??
        responseData?.total_amount ??
        responseData?.total
    );

    return {
      amountSubtotal: amountSubtotal ?? fallbackSubtotal,
      grandTotal: grandTotal ?? amountSubtotal ?? fallbackTotal,
    };
  };

  const handleBackToStart = () => {
    // window.history.replaceState({}, document.title, window.location.pathname);
    // navigate({ search: "" });
    // setAmount("");
    setIsEdit(false);
    // setEditVal("0.00");
    setPaymentProcess(null);
    setEmail("");
    setInvoiceId("");
    setScheduleId("");
    setOrderId("");
    setHideButtons(false);
    setScheduleDate(null);
    setIsScreenLoading(false);
    dispatch(onSetFinalDisclosureLoading(false));
    setValues(initialValues);
    setOfferApiCaalled(false);
    setCurrentStep(1);
    dispatch(onSetShowDisclosure(false));
    setInitialDisclosureAccept(false);
  };

  const resetMakePaymentState = useCallback(() => {
    setData({
      paymentPlan: {
        amount: 0,
        transactions: [],
        amount_subtotal: 0,
        grand_total: 0,
        payment_schedule: "",
        no_of_transaction: 5,
        transaction_type: "amount",
        start_date: getStartDate(),
        schedule_amount: "fixed",
        schedule_fld: "daily",
      },
    });

    setOfferApiCaalled(false);
    setCurrentStep(1);
    setValues(initialValues);
    setSelectedMethod("Card");
    setTab("one_time");
    setIsEdit(false);
    setEditVal("0.00");
    setAmount("");
    setPaymentProcess(null);
    setScheduleId("");
    setFailedReason(null);
    setHideButtons(false);
    setScheduleDate(null);
    setIsStartDate(true);
    setAvailableOffer(true);
    setAvailableOfferData([]);
    setInitialDisclosureAccept(false);
    setIsAddNewCard(false);
    setSelectedOffer(undefined);
    setAvailableStateLoading(false);
    setIsRealTime(false);
    setActiveOffer("");
    setIsLoading(true);
    latestScheduleRequestRef.current += 1;
    setScheduleRequestId(0);
    setIsApiCall(false);
    setIsEndDateError(false);
    setDateErrMsg(null);
    setScheduleCreateLoading(false);
    setSavedCardsEmail("");
    setShowPreview(false);
    setPaymentDetailsMain(undefined);
    setBudgetCalculatorIncome(0);
    setOfferAmount(0);
    setIsGoogleLoading(false);
    setIsScreenLoading(false);
    setPaymentMethodList(undefined);
    setIsWirePayment(false);
    setIsConfirmButtonDisable(true);
    setIsCustomPlan(false);
    setOfferStep(1);
    setScheduleAmountErr(null);
    setWireDetails({
      email: "",
      bank: "",
      routing: "",
      account: "",
      swift: "",
      dueDate: "",
    });
    setShowDetails(true);
    setIsSelectStartDate(false);
    setAmtErr(null);
    setIsManualForm(true);
    setUpdatedArr({
      updatedValue: [],
      createdValue: [],
      deletedValue: [],
    });
    setScheduleMethod("CC");

    dispatch(resetDuplicateOrder());
    dispatch(resetCheckDuplicatePayment());
    dispatch(updateCheckAmountState(false));
    dispatch(onSetShowDisclosure(false));
    dispatch(onSetFinalDisclosureLoading(false));
    dispatch(setIsPaymentProcessing(false));

    if (
      portalSettings &&
      portalSettings?.virtual_debt_collector === SettingsValue.YES &&
      accountData?.balance > 0
    ) {
      handlePaymentOfferGet();
    } else {
      setAvailableOffer(false);
      console.log("2 ======", 2);
      setTab("one_time");
      setIsLoading(false);
    }
  }, [dispatch]);

  const handleSendAssistanceRequest = useCallback(
    async (income: number, expense: number, netIncome: number) => {
      setIsAssistanceLoading(true);
      try {
        let payload = {
          income,
          expense,
          netIncome,
        };

        await dispatch(sendPaymentAssistanceRequest(payload)).unwrap();
        setIsAssistanceRequested(true);
        console.log("payload ======", payload);
      } catch (error) {
        console.error("Failed to send assistance request:", error);
      } finally {
        setIsAssistanceLoading(false);
      }
    },
    [dispatch]
  );

  const sendEmail = async ({
    emails,
    shouldSendAll,
    isFuture,
    isRealTime,
    schedule_id,
    invoice_id,
  }: any) => {
    try {
      const validEmails = Array.isArray(emails)
        ? emails
            .map((email: any) =>
              typeof email === "string" ? email.trim() : ""
            )
            .filter(Boolean)
        : [];
      const paymentId = isRealTime ? invoice_id : schedule_id;

      if (!accountData?.accountid || !paymentId || validEmails.length === 0) {
        console.warn("sendEmail skipped: missing required values", {
          accountid: accountData?.accountid,
          invoice_id,
          schedule_id,
          emails,
          isRealTime,
        });
        return;
      }

      let payload = {};
      if (!isRealTime) {
        payload = {
          schedule_id: schedule_id,
          accountid: accountData?.accountid,
          emails: validEmails,
          shouldSendAll: shouldSendAll,
          __abortSignal: getAbortSignal(),
        };
      } else {
        payload = {
          invoice_id: invoice_id,
          accountid: accountData?.accountid,
          emails: validEmails,
          isFuture: isFuture,
          shouldSendAll: shouldSendAll,
          __abortSignal: getAbortSignal(),
        };
      }
      await dispatch(paymentDownload(payload)).unwrap();
    } catch (error) {
      console.error("sendEmail", error);
    }
  };

  const processPaymentLink = async (values: any) => {
    const { lead_id } = accountLeadData || {};

    const {
      clusterid,
      lead_owners,
      balance,
      first_name,
      last_name,
      accountid,
    } = accountData || {};

    const {
      x_payment_method,
      x_city,
      x_address,
      address2,
      x_zip,
      x_state,
      x_country,
      email,
      iconType,
      x_card_num,
      x_card_code,
      x_exp_date,
      choldername,
      routing_number,
      account_number,
      bankName,
      bank,
      cc_token,
      cc_token2,
      stripe_token,
      cppid,
      pc_atid,
    } = values;

    let custom_conv = "on";

    const payload: Payload = {
      lead_id,
      cluster_id: clusterid,
      agent: lead_owners,
      balance,
      lead_owner: lead_owners,
      x_payment_method,
      city: x_city,
      address1: x_address,
      address2,
      zipcode: x_zip,
      state: x_state,
      country: x_country,
      first_name,
      last_name,
      accountid,
      isrecurring: "N",
      amount: offerAmount
        ? offerAmount
        : budgerCalculatorIncome
          ? budgerCalculatorIncome
          : parseFloat(editVal).toString().replace(/,/g, ""),
      email,
      amount_type: "amount",
      use_old_card: "1",
      description: "Online Payment",
      payment_note: "One-time",
      customer_device: getOperatingSystem(),
      timezone: getBrowserTimezoneOffset(),
      customer_timezone: getBrowserTimezoneName(),
      card_type: iconType ? iconType : "",
      old_cardnumber: x_card_num ? x_card_num?.replaceAll(" ", "") : "",
      old_cvv: x_card_code ? x_card_code : "",
      old_expiry: x_exp_date ? x_exp_date : "",
      old_4digit: x_card_num ? x_card_num?.slice(-4) : "",
      old_type: iconType ? iconType : "",
      old_zip: x_zip ? x_zip : "",
      cvv: x_card_code ? x_card_code : "",
      cexpiry: x_exp_date ? x_exp_date : "",
      ccardnumber: x_card_num ? x_card_num?.replaceAll(" ", "") : "",
      choldername: choldername ? choldername : "",
      aholdername: choldername ? choldername : "",
      routing_number: routing_number ? routing_number : "",
      account_number: account_number ? account_number : "",
      bank_name: bankName ? bankName : "",
      bank_address: bank ? bank : "",
      cc_token: cc_token ? cc_token : "",
      cc_token2: cc_token2 ? cc_token2 : "",
      stripe_token: stripe_token ? stripe_token : "",
      cppid: cppid ? cppid : 0,
      pc_atid: pc_atid ? pc_atid : 0,
      phone_number: accountData.phone,
      datefield: [],
      amountfield: [],
      percentagefield: [],
      custom_conv: custom_conv,
      ...offerLinkage,
      __abortSignal: getAbortSignal(),
    };

    try {
      dispatch(setIsPaymentProcessing(true));
      const res: any = await dispatch(makePayment(payload)).unwrap();
      if (res.body.success) {
        const { data } = res.body;
        const emails = [data.email];
        setEmail(data.email);
        setAmount(data.total_amount);
        setInvoiceId(data.invoice_id);
        setOrderId(data?.invoice_id);
        setActiveOffer("");
        const pay = {
          emails,
          invoice_id: data.invoice_id,
          isRealTime: true,
        };
        await sendEmail(pay);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: data?.email,
            event_status: "succeeded",
            activity_type: "payment_activity",
            clusterid: clusterId,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: offerAmount
              ? offerAmount
              : budgerCalculatorIncome
                ? budgerCalculatorIncome
                : editVal.replace(/,/g, ""),
            order_id: data?.invoice_id,
          })
        );
        setPaymentProcess("true");
      } else {
        if (res?.body?.data?.invoice_id) {
          const { invoice_id } = res?.body?.data;

          let pay = {
            invoice_id,
            emails: [res?.body?.data?.email || email],
            isRealTime: true,
          };
          await sendEmail(pay);
          setInvoiceId(invoice_id);
          setOrderId(invoice_id);

          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "failed",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: offerAmount
                ? offerAmount
                : budgerCalculatorIncome
                  ? budgerCalculatorIncome
                  : editVal.replace(/,/g, ""),
              order_id: invoice_id,
            })
          );
        }
        setPaymentProcess("false");
        setFailedReason(res?.body?.data?.msg || res?.body?.data?.message);
        setAmount(
          offerAmount
            ? `${offerAmount}`
            : budgerCalculatorIncome
              ? `${budgerCalculatorIncome}`
              : editVal.replace(/,/g, "")
        );
      }
    } catch (error: any) {
      console.error("Payment processing error:", error);
      setPaymentProcess("false");
      setFailedReason("Payment failed: Server Error");
    } finally {
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const getAdressValue = (payload: any) => {
    return {
      x_city: payload?.x_city ?? "",
      x_address: payload?.x_address ?? "",
      address2: payload?.address2 ?? "",
      x_zip: payload?.x_zip ?? "",
      x_state: payload?.x_state ?? "",
      x_country: payload?.x_country ?? "",
    };
  };

  const handleCreateNewCard = async (
    payload: any,
    schedulePayment: boolean = false
  ) => {
    const { x_city, x_address, address2, x_zip, x_state, x_country } =
      getAdressValue(payload);
    const payloads = {
      x_card_num: payload?.x_card_num?.replaceAll(" ", ""),
      x_exp_date: payload?.x_exp_date,
      x_card_code: payload?.x_card_code,
      choldername: payload?.choldername,
      x_payment_method: payload?.x_payment_method,
      x_city,
      x_address,
      address2,
      x_zip,
      x_state,
      x_country,
      routing_number: payload?.routing_number,
      account_number: payload?.account_number,
      bank_name: payload?.bankName,
      swift_code: payload?.swift_code,
      card_funding_type: payload?.card_funding_type,
      default: "Y",
      default_profile: "N",
      cluster_id: accountData?.clusterid,
      accountid: accountData?.accountid,
      x_first_name: accountData?.first_name,
      x_last_name: accountData?.last_name,
      x_phone: accountData?.phone || "",
      x_email: accountData?.email || "",
      lead_id: accountLeadData?.lead_id || "",
      gateway_id:
        payload?.x_payment_method === "CC"
          ? accountLeadData?.gatewayIdCard
          : accountLeadData?.gatewayIdACH || "",
      gateway_name:
        payload?.x_payment_method === "CC"
          ? accountLeadData?.cc_gateway_name
          : accountLeadData?.ach_gateway_name || "",
      // `payload` represents the card currently being saved.  The shared
      // form state can still contain a previously selected payment method's
      // name when a schedule is created, so it must not take precedence.
      account_holder_name: payload?.choldername ?? values?.choldername ?? "",
      card_type: payload?.iconType,
      accounts_id: accountData?.id,
      pc_atid: payload?.pc_atid,
    };

    try {
      const res: any = await dispatch(addCard(payloads)).unwrap();
      if (!schedulePayment) {
        if (res && res.body && res.body.success) {
          if (payload.x_payment_method === "WTO") {
            await paymentViaWire();
          } else {
            await processPaymentLink({
              ...payload,
              x_city,
              x_address,
              address2,
              x_zip,
              x_state,
              x_country,
              cc_token: res?.body?.data?.paymentMethod.cc_token,
              cc_token2:
                res?.body?.data?.paymentMethod.cc_token2 ??
                res?.body?.data?.paymentMethod.cc_token,
              cppid: res?.body?.data?.paymentMethod.id,
              stripe_token: res?.body?.data?.paymentMethod.cc_token,
            });
          }
        } else {
          setHideButtons(true);
          setPaymentProcess("false");
          setFailedReason("Failed to Pay with this method");
        }
      } else {
        if (res && res.body && res.body.success) {
          return { paymentMethod: res?.body?.data?.paymentMethod, payloads };
        } else {
          setHideButtons(true);
          setPaymentProcess("false");
          setFailedReason("Failed to Pay with this method");
        }
      }
    } catch (error: any) {
      setHideButtons(true);
      setPaymentProcess("false");
      setFailedReason("Failed to Pay with this method");
    }
  };

  const handlePaymentOfferGet = useCallback(async () => {
    setAvailableStateLoading(true);
    setOfferApiCaalled(true);
    try {
      const res = (await dispatch(
        paymentOfferGet({
          clusterid: accountData?.clusterid,
          accounts_id: accountData?.id,
          accountid: accountData?.accountid,
          __abortSignal: getAbortSignal(),
        })
      ).then(unwrapResult)) as PaymentOfferResponse;
      if (
        res &&
        res.body &&
        Array.isArray(res.body.data) &&
        res.body.data.length > 0
      ) {
        setAvailableOfferData(res.body.data);
        setIsLoading(false);
      } else {
        setAvailableOfferData([]);
        setAvailableOffer(false);
      }
    } catch (error) {
      console.error("Error fetching payment offer:", error);
      setAvailableOfferData([]);
      setAvailableOffer(false);
    } finally {
      setAvailableStateLoading(false);
    }
  }, [dispatch, accountData]);

  const onGetPaymentData = async () => {
    setIsLoading(true);
    try {
      let holidays = holidayList;

      if (
        getOffDaysPayments() !== "Y" &&
        (!holidays || holidays.length === 0)
      ) {
        const holidayResponse: any = await dispatch(
          getHolidayList({ date: moment().format("MM/DD/YYYY") })
        ).unwrap();
        holidays = holidayResponse?.body?.success
          ? holidayResponse?.body?.data || []
          : [];
      }

      let payload = {
        amount: null,
        balance: calculateTotal(accountData?.balance, selectedOffer),
        cluster_id: accountData?.clusterid,
        lead_id: accountLeadData?.lead_id,
        no_of_transaction:
          portalSettings?.transaction_limit < 5
            ? portalSettings?.transaction_limit
            : 5,
        off_days_payments: getOffDaysPayments(),
        payment_method: "",
        start_date: getStartDate(undefined, holidays),
        schedule_amount: "fixed",
        schedule_fld: "daily",
      };
      await dispatch(getPaymentNewPlan(payload)).unwrap();
      setIsLoading(false);
    } catch (error: any) {
      //  setIsTableLoading(false);
      setIsLoading(false);
      console.error("get payment history err: -->", error?.message);
    }
  };

  const createSchedulePaylod = (
    values: any,
    payloads: any,
    isCCTokenAvailable: boolean = false
  ) => {
    let payload = {};
    if (isAddNewCard) {
      let method = "CC";
      if (
        dataPayment?.selectedBank &&
        Object.keys(dataPayment.selectedBank).length > 0
      ) {
        method = "ACH";
      }
      if (
        dataPayment?.selectedWallet &&
        Object.keys(dataPayment.selectedWallet).length > 0
      ) {
        method = "WT";
      }

      let selectedData;
      if (method === "CC") {
        selectedData = dataPayment?.selectedCard;
      } else if (method === "WT") {
        selectedData = dataPayment?.selectedWallet;
        method = selectedData?.payment_method;
      } else {
        selectedData = dataPayment?.selectedBank;
      }

      let custom_conv = "on";
      // if (method === "CC") {
      //   custom_conv =
      //     paymentPermission?.cc_gateway_name?.name === PaymentGateway.STAX
      //       ? "off"
      //       : "on";
      // } else if (method === "ACH") {
      //   custom_conv =
      //     paymentPermission?.ach_gateway_name?.name === PaymentGateway.STAX
      //       ? "off"
      //       : "on";
      // } else if (method === "PP") {
      //   custom_conv = "off";
      // }

      payload = {
        module_id: "process_payment",
        lead_id: accountLeadData?.lead_id,
        cluster_id: accountData?.clusterid,
        agent: accountData?.lead_owner || "",
        isrecurring: "Y",
        balance: accountData?.balance,
        lead_owner: accountData?.lead_owner || "",
        max_transaction: data.paymentPlan.no_of_transaction || 5,
        end_date:
          data?.paymentPlan?.transactions?.[
            data.paymentPlan.no_of_transaction - 1
          ].date || moment().add(5, "days").format("MM/DD/YYYY"),
        start_date: data.paymentPlan.start_date
          ? new Date(data.paymentPlan.start_date).toLocaleDateString("en-US")
          : moment().format("MM/DD/YYYY"),
        x_payment_method: method,
        city: selectedData?.billing_city,
        address1: selectedData?.billing_address,
        address2: selectedData?.billing_address2,
        zipcode: selectedData?.billing_zip,
        state: selectedData?.billing_state,
        country: selectedData?.billing_country,
        phone_number: accountData?.phone || "",
        first_name: accountData?.first_name,
        last_name: accountData?.last_name,
        accountid: accountData?.accountid,
        amount:
          getEffectiveOfferInstallmentAmount() ??
          (data?.paymentPlan?.amount || editVal.replace(/,/g, "") || 0),
        cfc: "0.00",
        email:
          method === "CC"
            ? savedCardsEmail || email
            : accountData?.email || email,
        cc_token: selectedData?.cc_token,
        stripe_token: selectedData?.cc_token,
        cc_token2: selectedData?.cc_token2,
        card_type: selectedData?.cc_type || "VISA",
        stripe_brand: selectedData?.cc_type || "VISA",
        schedule: data?.paymentPlan?.schedule_fld || "daily",
        datefield: data?.paymentPlan?.transactions?.map((transaction: any) =>
          moment(
            transaction?.date ||
              transaction?.payment_date ||
              transaction?.billing_date
          ).format("MM/DD/YYYY")
        ),
        amountfield: data?.paymentPlan?.transactions?.map(
          (transaction: any) => transaction.amount || ""
        ),
        percentagefield:
          data?.paymentPlan?.transactions?.map(
            (transaction: any) => transaction.amount || ""
          ) || [],
        // transaction_type only applies to Custom plans; for Fixed/Auto the
        // server must never treat the row amounts as percentages.
        amount_type:
          data?.paymentPlan?.schedule_amount === "custom"
            ? data?.paymentPlan?.transaction_type || "amount"
            : "amount",
        schedule_type: data?.paymentPlan?.schedule_amount || "fixed",
        exp: selectedData?.cc_expiry,
        type: selectedData?.cc_type || "VISA",
        cdesc: "",
        list_id: "",
        sms_template_id: "",
        use_old_card: "1",
        cppid: selectedData?.id,
        old_cardnumber: selectedData?.account_number,
        old_cvv: selectedData?.cc_cvv,
        old_expiry: selectedData?.cc_expiry,
        old_4digit: selectedData?.cc_4digit,
        old_type: selectedData?.cc_type,
        old_zip: selectedData?.billing_zip,
        description: "Online Payment",
        payment_note: "Custom Plan",
        ip_address: "",
        cvv: selectedData?.cc_cvv,
        cexpiry: selectedData?.cc_expiry,
        ccardnumber: selectedData?.cc_4digit,
        choldername: selectedData?.account_name,
        aholdername: selectedData?.account_name,
        routing_number: selectedData?.routing_number,
        account_number: selectedData?.account_number,
        bank_name: selectedData?.bank_name,
        bank_address: "",
        custom_conv,
        ...offerLinkage,
        customer_device: getOperatingSystem(),
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
      };
    } else {
      payload = {
        module_id: "process_payment",
        lead_id: accountLeadData?.lead_id,
        cluster_id: accountData?.clusterid,
        agent: accountData?.lead_owner || "",
        isrecurring: "Y",
        balance: accountData?.balance,
        lead_owner: accountData?.lead_owner || "",
        max_transaction: data.paymentPlan.no_of_transaction || 5,
        end_date:
          data?.paymentPlan?.transactions?.[
            data.paymentPlan.no_of_transaction - 1
          ].date || moment().add(5, "days").format("MM/DD/YYYY"),
        start_date: data.paymentPlan.start_date
          ? new Date(data.paymentPlan.start_date).toLocaleDateString("en-US")
          : moment().format("MM/DD/YYYY"),
        x_payment_method:
          (isCCTokenAvailable
            ? values?.x_payment_method
            : payloads?.x_payment_method) || "CC",
        city: (isCCTokenAvailable ? values?.x_city : payloads?.x_city) || "",
        address1:
          (isCCTokenAvailable ? values?.x_address : payloads?.x_address) || "",
        address2:
          (isCCTokenAvailable ? values?.address2 : payloads?.address2) || "",
        zipcode: (isCCTokenAvailable ? values?.x_zip : payloads?.x_zip) || "",
        state: (isCCTokenAvailable ? values?.x_state : payloads?.x_state) || "",
        country:
          (isCCTokenAvailable ? values?.x_country : payloads?.x_country) || "",
        phone_number: accountData?.phone || "",
        first_name: accountData?.first_name,
        last_name: accountData?.last_name,
        accountid: accountData?.accountid,
        amount:
          getEffectiveOfferInstallmentAmount() ??
          (data?.paymentPlan?.amount || editVal.replace(/,/g, "") || 0),
        cfc: "0.00",
        email:
          (isCCTokenAvailable
            ? values?.email
            : payloads?.x_email || payloads?.email) || "",
        cc_token: values?.cc_token,
        stripe_token: values?.cc_token,
        cppid: values?.cppid,
        pc_atid: values?.pc_atid,
        cc_token2: values?.cc_token2 ?? values?.cc_token,
        card_type: values?.iconType,
        stripe_brand: values?.iconType,
        schedule: data?.paymentPlan?.schedule_fld || "daily",
        datefield: data?.paymentPlan?.transactions?.map((transaction: any) =>
          moment(
            transaction?.date ||
              transaction?.payment_date ||
              transaction?.billing_date
          ).format("MM/DD/YYYY")
        ),
        amountfield: data?.paymentPlan?.transactions?.map(
          (transaction: any) => transaction.amount || ""
        ),
        percentagefield:
          data?.paymentPlan?.transactions?.map(
            (transaction: any) => transaction.amount || ""
          ) || [],
        // transaction_type only applies to Custom plans; for Fixed/Auto the
        // server must never treat the row amounts as percentages.
        amount_type:
          data?.paymentPlan?.schedule_amount === "custom"
            ? data?.paymentPlan?.transaction_type || "amount"
            : "amount",
        schedule_type: data?.paymentPlan?.schedule_amount || "fixed",
        exp: values?.cc_expiry,
        type: values?.iconType,
        cdesc: "",
        list_id: "",
        sms_template_id: "",
        use_old_card: "0",
        old_cardnumber: values.x_card_num?.replaceAll(" ", ""),
        old_cvv: values?.x_card_code,
        old_expiry: values?.cc_expiry,
        old_4digit: values.x_card_num?.replaceAll(" ", "").slice(-4),
        old_type: values?.iconType,
        old_zip: payloads?.x_zip || "",
        description: "Online Payment",
        payment_note: "Custom Plan",
        ip_address: "",
        cvv: values?.x_card_code,
        cexpiry: values?.cc_expiry,
        ccardnumber: values.x_card_num?.replaceAll(" ", ""),
        choldername: values?.choldername,
        aholdername: values?.choldername,
        routing_number: values?.routing_number,
        account_number: values?.account_number,
        bank_name: values?.bank_name,
        bank_address: "",
        ...offerLinkage,
        customer_device: getOperatingSystem(),
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
      };
    }

    return payload;
  };

  const handleSchedulePayment = async ({
    value,
    isCCTokenAvailable = false,
    addNewCardData,
  }: {
    value: any;
    isCCTokenAvailable?: boolean;
    addNewCardData: any;
  }) => {
    const updatedArr: any[] = [];
    const createdArr: any[] = [];
    const deletedArray: any[] = [];
    try {
      dispatch(setIsPaymentProcessing(true));
      const { paymentMethod, payloads } = value;
      // Separate transactions into updated and created arrays
      data?.paymentPlan?.transactions?.forEach((item: any) => {
        if (item.id) {
          updatedArr.push(item);
        } else {
          createdArr.push(item);
        }
      });

      // Determine deleted transactions
      paymentDetailsMain?.forEach((item: any) => {
        const existsInSecondArray = updatedArr.some(
          (secondItem: any) => secondItem.id === item.id
        );
        if (!existsInSecondArray) {
          deletedArray.push(item.id);
        }
      });

      const payload: any = createSchedulePaylod(
        { ...addNewCardData, ...paymentMethod },
        payloads,
        isCCTokenAvailable
      );
      payload.__abortSignal = getAbortSignal();
      const response: any = await dispatch(makePayment(payload)).unwrap();

      if (response?.body?.success) {
        const { schedule_id, invoice_id } = response?.body?.data;
        setHideButtons(true);
        setPaymentProcess("true");
        setEmail(response?.body?.data?.email);
        setAmount(response?.body?.data?.total_amount);
        let isRealTime = true;
        if (
          response?.body?.data?.date &&
          !moment(response?.body?.data?.date).isSame(moment(), "days")
        ) {
          isRealTime = false;
        }
        setScheduleDate(response?.body?.data?.date);
        setInvoiceId(schedule_id);
        if (tab !== "one_time") {
          setScheduleId(schedule_id);
        }
        setOrderId(invoice_id);
        if (tab === "one_time") {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: data?.email,
              event_status: "succeeded",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: response?.body?.data?.amount,
              order_id: invoice_id,
            })
          );
        } else {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "custom_plan",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: data?.email,
              event_status: "created",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount:
                response?.body?.data?.totalTransactionAmount ||
                response?.body?.data?.amount,
              sch_id: schedule_id,
            })
          );
          if (isRealTime) {
            await dispatch(
              addNotification({
                initiated_by: "contact",
                event_type: "payment_processed",
                userid: userData?.data?.accountData?.leadData?.user_id,
                email: data?.email,
                event_status: "succeeded",
                activity_type: "payment_activity",
                clusterid: clusterId,
                communication_status: "",
                accounts_id: userData?.data?.accountData?.id,
                payment_amount: response?.body?.data?.amount,
                order_id: invoice_id,
              })
            );
          }
        }

        const values = {
          emails: [response?.body?.data?.email || ""],
          schedule_id,
          invoice_id,
          isRealTime,
          isFuture: true,
        };
        await sendEmail(values);
      } else {
        let payment_amount = payload?.amountfield?.[0] ?? 0;
        if (
          payload?.amount_type === "percent" &&
          data?.paymentPlan?.percentage_amounts
        ) {
          payment_amount = data?.paymentPlan?.percentage_amounts?.[0];
        }
        if (response?.body?.data?.invoice_id) {
          const { invoice_id } = response?.body?.data;
          setScheduleDate(response?.body?.data?.date);
          let pay = {
            invoice_id,
            emails: [response?.body?.data?.email || payload?.email],
            isRealTime: true,
          };
          await sendEmail(pay);
          setInvoiceId(invoice_id);
          setOrderId(invoice_id);

          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "failed",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: payment_amount,
              order_id: invoice_id,
            })
          );
        }

        setAmount(payment_amount);

        if (
          payload?.datefield &&
          moment(payload?.datefield?.[0]).isSame(moment(), "days")
        ) {
          setIsRealTime(true);
        }

        setHideButtons(true);
        setPaymentProcess("false");
        setFailedReason(
          response?.body?.data?.msg || response?.body?.data?.message
        );
        setScheduleCreateLoading(false);
      }
    } catch (error) {
      console.log("handleSchedulePayment error =====", error);
      setHideButtons(true);
      setPaymentProcess("false");
      setScheduleCreateLoading(false);
    } finally {
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const handleSchedulePaymentCreate = async ({
    isPlaid,
    isCCTokenAvailable = false,
    plaidBankDetails,
  }: {
    isPlaid?: boolean;
    isCCTokenAvailable?: boolean;
    plaidBankDetails?: any;
  }) => {
    const amountError = getCurrentScheduleAmountError();
    setScheduleAmountErr(amountError);
    if (amountError) return;

    const selectedOfferEndDateError = getCurrentSelectedOfferEndDateError();

    if (
      selectedOfferTransactionError ||
      isEndDateError ||
      selectedOfferEndDateError
    ) {
      if (selectedOfferEndDateError) {
        setIsEndDateError(true);
        setDateErrMsg(selectedOfferEndDateError.message);
      }
      return;
    }

    if (!isPlaid) {
      //check negative balance
      let todayAmount = Number(getTodayAmount());
      let negativeBalance =
        !checkAmount && todayAmount > 0
          ? await checkOverPaymentAmount({
              amount: todayAmount,
              balance: userData?.data?.accountData?.balance,
              overpayment_protection: portalSettings?.overpayment_protection,
              dispatch,
            })
          : true;
      if (!negativeBalance) {
        return;
      }

      //check duplicate payment and disclousre
      let moveAhead = await handleCheckDisclosureAndDuplicate({ hide: true });
      if (!moveAhead) {
        return;
      }
    }
    dispatch(updateCheckAmountState(false));

    let addNewCardData = !isPlaid
      ? dataPayment?.paymentValues
      : dataPayment?.selectedWallet
        ? dataPayment?.selectedWallet
        : plaidBankDetails;
    try {
      let value: any = {};
      setScheduleCreateLoading(true);

      if (!isAddNewCard && !isCCTokenAvailable) {
        value = await handleCreateNewCard(addNewCardData, true);
      }

      await handleSchedulePayment({
        value,
        isCCTokenAvailable,
        addNewCardData,
      });
    } catch (error) {
      console.error("Error in handleSchedulePaymentCreate:", error);
    } finally {
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  useEffect(() => {
    if (paymentLogo && clusterId) onGetLogo();
  }, [paymentLogo, clusterId]);

  useEffect(() => {
    setOfferAmount(
      accountData?.balance ===
        calculateTotal(accountData?.balance, selectedOffer)
        ? 0
        : calculateTotal(accountData?.balance, selectedOffer)
    );
  }, [selectedOffer]);

  const getPaymentMethodPermission = async () => {
    try {
      let payload = {
        accountid: userData?.data?.accountData?.accountid,
        clusterid: clusterId,
        lead_id: accountLeadData?.lead_id,
        __abortSignal: getAbortSignal(),
      };
      await dispatch(getpaymentPermission(payload));
    } catch (error) {
      console.log("getPaymentMethodPermission error =====", error);
    }
  };

  // wire payment
  const paymentViaWire = async () => {
    try {
      let payload = {
        payment_gateway_id: "",
        amount: parseFloat(editVal).toFixed(2),
        clusterid: clusterId,
        x_method: "WTO",
        lead_id: accountLeadData?.lead_id,
        email: userData?.data?.accountData?.email,
        accountid: userData?.data?.accountData?.accountid,
        description: "Online Payment",
        payment_note: tab === "one_time" ? "One-time" : "Custom Plan",
        ivr_type: "CUST_PRTL",
        __abortSignal: getAbortSignal(),
      };
      let res: any = await dispatch(wirePaymentProcess(payload)).unwrap();
      if (res.body.success) {
        setWireDetails({
          email: res?.body?.data?.email || "",
          bank: res?.body?.data?.bank_name || "",
          routing: res?.body?.data?.routing_number || "",
          account: res?.body?.data?.account_number || "",
          swift: res?.body?.data?.swift_code || "",
          dueDate:
            moment(
              moment().add(res?.body?.data?.wire_deadline || 0, "days")
            ).format("MM/DD/YYYY") || "",
        });
        setInvoiceId(res?.body?.data?.invoice_id);
        setOrderId(res?.body?.data?.invoice_id);
        setEmail(res?.body?.data?.email);
        setIsWirePayment(true);
        setPaymentProcess("true");
        let pay = {
          invoice_id: res?.body?.data?.invoice_id,
          emails: [res?.body?.data?.email],
          isRealTime: true,
        };
        await sendEmail(pay);
      } else {
        setFailedReason(res?.body?.data?.msg);
        setPaymentProcess("false");
      }
    } catch (error) {
      console.log(" paymentViaWire error=====", error);
    } finally {
      setAmount(editVal);
    }
  };

  useEffect(() => {
    if (isLogin) {
      getPaymentMethodPermission();
    }
  }, [isLogin]);

  useEffect(() => {
    if (!availableOffer && !selectedOffer) {
      onGetPaymentData();
    }
  }, [availableOffer]);
  useEffect(() => {
    if (!paymentProcess && !offerApiCalled && !isScreenLoading) {
      if (
        portalSettings &&
        portalSettings?.virtual_debt_collector === SettingsValue.YES &&
        accountData?.balance > 0
      ) {
        handlePaymentOfferGet();
      } else {
        setAvailableOffer(false);
        // console.log("3 ======", 3);
        // setTab("one_time");
        setIsLoading(false);
      }
    }
  }, [portalSettings, accountData]);

  useEffect(() => {
    if (paymentPlanDataRedux && Object.keys(paymentPlanDataRedux)?.length > 0) {
      const calculatedDataInitial = calculateOutstanding(
        paymentPlanDataRedux?.date_array
      );
      const scheduleTotals = getScheduleResponseTotals(
        paymentPlanDataRedux,
        calculatedDataInitial?.totalAmount
      );

      setData((prevData: any) => ({
        ...prevData,
        paymentPlan: {
          ...prevData.paymentPlan,
          transactions: paymentPlanDataRedux?.date_array,
          grand_total: scheduleTotals.grandTotal,
          amount_subtotal: scheduleTotals.amountSubtotal,
          payment_schedule:
            paymentPlanDataRedux?.clientCustomerData?.payment_schedule,
          no_of_transaction: paymentPlanDataRedux?.date_array?.length,
          start_date:
            paymentPlanDataRedux?.date_array[0]?.billing_date ??
            moment().format("MM/DD/YYYY"),
          amount: paymentPlanDataRedux?.date_array[0]?.amount,
          schedule_amount: paymentPlanDataRedux?.date_array[0]?.schedule_type,
          schedule_fld:
            paymentPlanDataRedux?.clientCustomerData?.payment_schedule,
        },
      }));
      setPaymentDetailsMain(paymentPlanDataRedux?.date_array);
    }
  }, [paymentPlanDataRedux]);

  //get plan dates
  useEffect(() => {
    if (!scheduleRequestId) return;

    const activeRequestId = scheduleRequestId;
    const normalizedScheduleAmount = `${
      data?.paymentPlan?.schedule_amount || ""
    }`.toLowerCase();
    const normalizedTransactionType = `${
      data?.paymentPlan?.transaction_type || ""
    }`.toLowerCase();
    const isCustomSchedule = normalizedScheduleAmount === "custom";
    const isPercentagePlan =
      ["p", "percent", "percentage"].includes(normalizedTransactionType) ||
      `${data?.paymentPlan?.transactions?.[0]?.amount_type || ""}`.toUpperCase() ===
        "P";
    const canonicalTransactionType = isPercentagePlan ? "percent" : "amount";

    const createPayload = () => {
      let isSelectedOffer =
        selectedOffer && Object.keys(selectedOffer).length > 0;

      let amount_total = isSelectedOffer
        ? calculateTotal(accountData?.balance, selectedOffer)
        : accountData?.balance;

      return {
        date_array: data.paymentPlan?.transactions?.map((transaction: any) => ({
          amount: transaction.amount || 0,
          balance: formatCurrency(transaction.balance),
          fee: "0.00",
          payment_date: moment(transaction.payment_date).format("MM/DD/YYYY"),
        })),
        lead_id: accountLeadData?.lead_id,
        cluster_id: accountData?.clusterid,
        balance: amount_total,
        schedule_amount: data?.paymentPlan?.schedule_amount,
        transaction_type: canonicalTransactionType,
        payment_method: "",
        no_of_transaction: data.paymentPlan?.no_of_transaction,
        grand_total: "0.00",
        fee_subtotal: "0.00",
        amount_total: `${amount_total}`,
        isStartDate: isSelectStartDate,
        isAmount: true,
        isDelete: false,
        start_date: data.paymentPlan.start_date,
        schedule_fld: data.paymentPlan?.schedule_fld || "daily",
        amount:
          getEffectiveOfferInstallmentAmount() ??
          (data?.paymentPlan?.schedule_amount === "auto"
            ? amount_total / data?.paymentPlan?.no_of_transaction
            : data?.paymentPlan?.amount
              ? Number(data?.paymentPlan?.amount?.replace(/,/g, ""))
              : 0),
        make_payment: true,
        shouldCallCustom: true,
      };
    };

    const updateTransactions = (
      transactions: any[],
      newTransactions: any[],
      noOfTransactions: number
    ): any[] => {
      try {
        const updatedTransArr = [];
        const startIdx = updatedTransArr.length;
        for (let i = startIdx, k = 0; i < noOfTransactions; i++, k++) {
          const newTransaction = newTransactions[k];
          if (newTransaction) {
            updatedTransArr.push({
              ...transactions[0],
              id: null,
              date:
                data?.paymentPlan?.schedule_amount === "custom" || isCustomPlan
                  ? newTransaction.payment_date
                  : newTransaction.date,
              billing_date:
                data?.paymentPlan?.schedule_amount === "custom" || isCustomPlan
                  ? newTransaction.payment_date
                  : newTransaction.date,
              amount: newTransaction?.amount,
              balance: newTransaction.balance,
              payment_date: newTransaction?.payment_date,
              amount_type:
                data.paymentPlan?.schedule_amount === "custom"
                  ? transactions[0].amount_type
                  : "amount",
              clusterid: accountData?.clusterid,
              status: "Pending",
              fee: newTransaction.fee,
            });
          } else {
            console.warn(`Missing new transaction for index ${k}`);
          }
        }

        return updatedTransArr;
      } catch (error) {
        console.error("Error updating transactions:", error);
        return transactions;
      }
    };

    const payload = createPayload();
    setIsLoading(true);

    dispatch(getPaymentPlanDates(payload))
      .unwrap()
      .then((res: any) => {
        if (activeRequestId !== latestScheduleRequestRef.current) return;

        if (res.status === 200) {
          const endDateError = getScheduleEndDateError(res?.body);
          const generatedTransactions = Array.isArray(
            res?.body?.data?.date_array
          )
            ? res.body.data.date_array
            : [];
          const updatedTransArr = isCustomSchedule
            ? mergeGeneratedCustomSchedule(
                data.paymentPlan.transactions,
                generatedTransactions,
                data.paymentPlan.no_of_transaction,
                canonicalTransactionType
              )
            : updateTransactions(
                data.paymentPlan.transactions,
                generatedTransactions,
                data.paymentPlan.no_of_transaction
              );
          const selectedOfferEndDateError =
            getCurrentSelectedOfferEndDateError(updatedTransArr);
          const hasEndDateError =
            endDateError.isInvalid || Boolean(selectedOfferEndDateError);

          setIsEndDateError(hasEndDateError);
          setDateErrMsg(
            hasEndDateError
              ? endDateError.message ||
                  selectedOfferEndDateError?.message ||
                  "This payment schedule exceeds the configured end-date limit"
              : null
          );

          const customScheduleSummary = isCustomSchedule
            ? calculateScheduleSummary(
                updatedTransArr,
                calculateTotal(accountData?.balance, selectedOffer),
                isPercentagePlan ? "percent" : "amount"
              )
            : null;
          const calculatedData = customScheduleSummary
            ? null
            : calculateOutstanding(
                updatedTransArr,
                data.paymentPlan?.transaction_type,
                accountData?.balance
              );
          const scheduleTotals = customScheduleSummary
            ? {
                amountSubtotal: customScheduleSummary.amountSubtotal,
                grandTotal: customScheduleSummary.grandTotal,
              }
            : getScheduleResponseTotals(
                res?.body?.data,
                calculatedData?.totalAmount || 0
              );

          setData((prevData: any) => ({
            ...prevData,
            paymentPlan: {
              ...prevData.paymentPlan,
              transactions: customScheduleSummary
                ? customScheduleSummary.dataArr
                : updatedTransArr,
              percentage_amounts:
                customScheduleSummary && isPercentagePlan
                  ? customScheduleSummary.percentageAmounts
                  : isCustomSchedule
                    ? []
                    : prevData.paymentPlan.percentage_amounts,
              amount:
                getEffectiveOfferInstallmentAmount() ??
                prevData.paymentPlan.amount,
              grand_total: scheduleTotals.grandTotal,
              amount_subtotal: scheduleTotals.amountSubtotal,
              schedule_fld:
                res?.body?.data?.schedule_fld ||
                prevData.paymentPlan.schedule_fld,
              schedule_amount: selectedOffer
                ? "auto"
                : isCustomSchedule
                  ? prevData.paymentPlan.schedule_amount
                  : res?.body?.data?.schedule_amount ||
                    prevData.paymentPlan.schedule_amount,
              transaction_type: canonicalTransactionType,
              no_of_transaction: data.paymentPlan.no_of_transaction,
            },
          }));

          setUpdatedArr({
            updatedValue: [],
            createdValue: [],
            deletedValue: [],
          });

          paymentPlanBeforeAddRef.current = null;
        } else if (paymentPlanBeforeAddRef.current) {
          const previousPaymentPlan = paymentPlanBeforeAddRef.current;
          setData((prevData: any) => ({
            ...prevData,
            paymentPlan: previousPaymentPlan,
          }));
          paymentPlanBeforeAddRef.current = null;
        }
      })
      .catch((e: any) => {
        if (activeRequestId !== latestScheduleRequestRef.current) return;

        if (paymentPlanBeforeAddRef.current) {
          const previousPaymentPlan = paymentPlanBeforeAddRef.current;
          setData((prevData: any) => ({
            ...prevData,
            paymentPlan: previousPaymentPlan,
          }));
        }
        paymentPlanBeforeAddRef.current = null;
      })
      .finally(() => {
        if (activeRequestId !== latestScheduleRequestRef.current) return;

        setIsApiCall(false);
        setIsLoading(false);
        setIsCustomPlan(false);
        setIsSelectStartDate(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleRequestId]);

  const getStartLimit = (): Date => {
    const current = portalSettings?.start_date_limit_external;
    const currentArr = current?.split("-");
    if (currentArr?.length === 2) {
      let days = Number(currentArr[0]);

      if (currentArr[1] === "W") {
        days = days * 7;
      }

      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() + days);

      return currentDate;
    } else {
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() + 365);

      return currentDate;
    }
  };

  // payment permission
  useEffect(() => {
    if (Object.keys(paymentPermission)?.length > 0) {
      let methodList = [];
      if (paymentPermission.cc_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "Card",
          icon: <CardIcon />,
        });
      }

      if (paymentPermission.ach_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "Bank",
          icon: <BankIconSm />,
        });
      }

      if (paymentPermission.wt_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "Wire",
          icon: <WireTransferIcon />,
        });
      }

      if (paymentPermission.pp_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "PayPal",
          icon: <PaypalIcon />,
        });
      }

      if (paymentPermission.capp_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "Cash App Pay",
          icon: <CashAppIcon />,
        });
      }
      if (paymentPermission.amzp_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "Amazon Pay",
          icon: <AmazonPay />,
        });
      }
      if (paymentPermission.cc_ext_availability === SettingsValue.YES) {
        setSelectedMethod(methodList?.[0]?.name);
      }

      setPaymentMethodList(methodList);
    } else {
      setPaymentMethodList(defaultPaymentMethodList);
    }
  }, [paymentPermission]);

  const { paymentPlan, conFee } = useAppSelector(
    (state: RootState) => state.payment
  );
  // google pay
  const handleLegacyGooglePay = async (tokenData: any, total_amount: any) => {
    try {
      setIsGoogleLoading(true);
      dispatch(setIsPaymentProcessing(true));
      const { tokenizationData } = tokenData;
      let resp = JSON.parse(tokenizationData.token);
      let payload;
      if (tab === "one_time") {
        payload = {
          email: accountData?.email || "",
          stripe_token: resp?.id,
          phone_number: accountData?.phone,
          first_name: accountData?.first_name || "",
          last_name: accountData?.last_name || "",
          accountid: accountData?.accountid || "",
          total_amount: total_amount,
          clusterid: accountData?.clusterid,
          address1: resp?.card?.address_line1 || "",
          address2: resp?.card?.address_line2 || "",
          city: resp?.card?.address_city || "",
          state: resp?.card?.address_state || "",
          zip: resp?.card?.address_zip || "",
          country: resp?.card?.address_country || "",
          is_recurring: "N",
          lead_id: accountLeadData?.lead_id,
          description: "Online Payment",
          payment_note: "One-time",
          module: "process_paymnet",
          x_payment_method: "GP",
          currency: "usd",
          card_type: resp?.card?.brand || "",
          cc_last4: resp?.card?.last4 || "",
          cc_expiry: resp?.card?.exp_month + "/" + resp?.card?.exp_year || "",
          ...offerLinkage,
          __abortSignal: getAbortSignal(),
        };
      } else {
        payload = {
          email: accountData?.email || "",
          stripe_token: resp?.id,
          phone_number: accountData?.phone,
          first_name: accountData?.first_name || "",
          last_name: accountData?.last_name || "",
          accountid: accountData?.accountid || "",
          description: "Online Payment",
          payment_note: "Custom Plan",
          total_amount:
            data.paymentPlan?.transaction_type === "amount"
              ? data.paymentPlan?.transactions?.reduce(
                  (accumulator: any, currentValue: any) =>
                    accumulator + parseFloat(currentValue.amount),
                  0
                )
              : data.paymentPlan?.percentage_amounts?.reduce(
                  (accumulator: any, currentValue: any) =>
                    accumulator + currentValue,
                  0
                ),
          clusterid: accountData?.clusterid,
          address1: resp?.card?.address_line1 || "",
          address2: resp?.card?.address_line2 || "",
          city: resp?.card?.address_city || "",
          state: resp?.card?.address_state || "",
          zip: resp?.card?.address_zip || "",
          country: resp?.card?.address_country || "",
          is_recurring: "Y",
          ...offerLinkage,
          lead_id: accountLeadData?.lead_id,
          module: "process_paymnet",
          x_payment_method: "GP",
          currency: "usd",
          card_type: resp?.card?.brand || "",
          cc_last4: resp?.card?.last4 || "",
          cc_expiry: resp?.card?.exp_month + "/" + resp?.card?.exp_year || "",
          amountfield:
            data.paymentPlan?.transactions?.map((item: any) => item.amount) ||
            [],
          datefield:
            data.paymentPlan?.transactions?.map((item: any) =>
              moment(
                item?.date || item?.payment_date || item?.billing_date
              ).format("MM/DD/YYYY")
            ) || [],
          percentagefield:
            data.paymentPlan?.transactions?.map((item: any) => item.amount) ||
            [],
          exclude_holidays: "N",
          schedule: data.paymentPlan?.schedule_fld,
          schedule_type: data.paymentPlan?.schedule_amount,
          total_payments: data.paymentPlan?.transactions?.length,
          lead_owner: userData?.data?.accountData?.lead_owner,
          agent: userData?.data?.accountData?.lead_owner,
          start_date: data.paymentPlan?.start_date,
          amount_type:
            data.paymentPlan?.schedule_amount === "custom"
              ? data.paymentPlan?.transaction_type || "amount"
              : "amount",
          __abortSignal: getAbortSignal(),
        };
      }
      let res: any = await dispatch(makeGooglePay(payload));
      if (res?.payload?.body.success) {
        const { schedule_id, invoice_id } = res?.payload?.body?.data;
        setPaymentProcess("true");
        setEmail(res?.payload?.body?.data?.email);
        setAmount(res?.payload?.body?.data?.total_amount);
        let isRealTime = true;
        if (
          res?.payload?.body?.data?.date &&
          !moment(res?.payload?.body?.data?.date).isSame(moment(), "days")
        ) {
          isRealTime = false;
          setScheduleDate(res?.payload?.body?.data?.date);
        }
        if (tab === "one_time") {
          setInvoiceId(invoice_id);
        } else {
          setInvoiceId(schedule_id);
          setScheduleId(schedule_id);
        }

        setOrderId(invoice_id);

        if (tab === "one_time") {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: data?.email,
              event_status: "succeeded",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: res?.payload?.body?.data?.amount,
              order_id: invoice_id,
            })
          );
        } else {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "custom_plan",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: data?.email,
              event_status: "created",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount:
                res?.payload?.body?.data?.totalTransactionAmount ||
                res?.payload?.body?.data?.amount,
              sch_id: schedule_id,
            })
          );
          if (isRealTime) {
            await dispatch(
              addNotification({
                initiated_by: "contact",
                event_type: "payment_processed",
                userid: userData?.data?.accountData?.leadData?.user_id,
                email: data?.email,
                event_status: "succeeded",
                activity_type: "payment_activity",
                clusterid: clusterId,
                communication_status: "",
                accounts_id: userData?.data?.accountData?.id,
                payment_amount: res?.payload?.body?.data?.amount,
                order_id: invoice_id,
              })
            );
          }
        }
        let pay = {
          invoice_id,
          schedule_id,
          emails: [res?.payload?.body?.data?.email],
          isRealTime,
          isFuture: tab !== "one_time",
        };
        await sendEmail(pay);
      } else {
        setScheduleDate(res?.payload?.body?.data?.date);
        setFailedReason(res?.payload?.body?.data?.message);

        if (res?.payload?.body?.data?.invoice_id) {
          setInvoiceId(res?.payload?.body?.data?.invoice_id);
          setOrderId(res?.payload?.body?.data?.invoice_id);

          let pay = {
            invoice_id: res?.payload?.body?.data?.invoice_id,
            emails: [res?.payload?.body?.data?.email],
            isRealTime,
            isFuture: tab !== "one_time",
          };
          await sendEmail(pay);
        }
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "succeeded",
            activity_type: "payment_activity",
            clusterid: clusterId,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: res?.payload?.body?.data?.amount,
            order_id: res?.payload?.body?.data?.invoice_id,
          })
        );
        setAmount(res?.payload?.body?.data?.total_amount);
      }
      setIsGoogleLoading(false);
    } catch (error: any) {
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      setIsGoogleLoading(false);
      console.log("handleGooglePay error =====", error);
    } finally {
      setHideButtons(true);
      dispatch(resetDuplicateOrder());
      dispatch(onSetShowDisclosure(false));
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const createStripeWalletPayload = (
    paymentIntent: any,
    paymentMethod: "AP" | "GP"
  ) => {
    if (tab === "one_time") {
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: accountData?.clusterid,
        isrecurring: "N",
        isSchedulePayment: false,
        x_payment_method: paymentMethod,
        amount: offerAmount
          ? offerAmount
          : budgerCalculatorIncome
            ? budgerCalculatorIncome
            : editVal.replace(/,/g, ""),
        accountid: accountData?.accountid || "",
        first_name: accountData?.first_name || "",
        last_name: accountData?.last_name || "",
        phone_number: accountData.phone,
        module: "process_payment",
        transaction_id: paymentIntent?.id,
        ...(paymentMethod === "AP" ? {} : { conv_fee: conFee }),
        description: "Online Payment",
        payment_note: "One-time",
        custom_conv: "on",
        ...offerLinkage,
      };
      return payload;
    } else {
      // Stripe wallets come directly from the schedule form. Use the plan that is
      // currently displayed instead of depending on the asynchronously stored
      // Redux copy used by older redirect-based wallet flows.
      const activePaymentPlan =
        paymentMethod === "GP" || paymentMethod === "AP"
          ? data.paymentPlan
          : paymentPlan;
      const activeAmountType =
        activePaymentPlan?.schedule_amount === "custom"
          ? activePaymentPlan?.transaction_type || "amount"
          : "amount";
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: accountData?.clusterid,
        isrecurring: "Y",
        isSchedulePayment: true,
        ...offerLinkage,
        x_payment_method: paymentMethod,
        phone_number: accountData.phone,
        description: "Online Payment",
        payment_note: "Custom Plan",
        amount:
          activeAmountType === "amount"
            ? activePaymentPlan?.transactions?.reduce(
                (accumulator: any, currentValue: any) =>
                  accumulator + parseFloat(currentValue.amount),
                0
              )
            : activePaymentPlan?.percentage_amounts?.reduce(
                (accumulator: any, currentValue: any) =>
                  accumulator + currentValue,
                0
              ),
        transaction_id: paymentIntent?.id,
        first_name: accountData?.first_name || "",
        last_name: accountData?.last_name || "",
        accountid: accountData?.accountid || "",
        module: "process_payment",
        amountfield:
          activePaymentPlan?.transactions?.map((item: any) => item.amount) ||
          [],
        datefield:
          activePaymentPlan?.transactions?.map((item: any) =>
            moment(
              item?.date || item?.payment_date || item?.billing_date
            ).format("MM/DD/YYYY")
          ) || [],
        percentagefield:
          activePaymentPlan?.transactions?.map((item: any) => item.amount) ||
          [],
        exclude_holidays: "N",
        email: accountData?.email || "",
        schedule: activePaymentPlan?.schedule_fld,
        schedule_type: activePaymentPlan?.schedule_amount,
        total_payments: activePaymentPlan?.transactions?.length,
        lead_owner: userData?.data?.accountData?.lead_owner,
        agent: userData?.data?.accountData?.lead_owner,
        start_date: activePaymentPlan?.start_date,
        amount_type: activeAmountType,
        conv_fee: conFee,
        custom_conv: "on",
      };
      return payload;
    }
  };

  const processStripeWalletPayment = async (
    success: boolean,
    paymentIntent: any,
    paymentMethod: "AP" | "GP"
  ) => {
    const verificationKey = `${paymentMethod}:${paymentIntent?.id || "missing"}`;
    if (walletVerificationInFlightRef.current.has(verificationKey)) {
      console.info(
        "[Stripe Wallet][make-payment] duplicate verification skipped",
        {
          paymentMethod,
        }
      );
      applePayDebug("warn", "duplicate verification skipped", {
        paymentMethod,
        intentId: paymentIntent?.id,
      });
      return;
    }
    walletVerificationInFlightRef.current.add(verificationKey);
    let verificationCompleted = false;
    try {
      dispatch(setIsPaymentProcessing(true));
      let payload = {
        ...createStripeWalletPayload(paymentIntent, paymentMethod),
        __abortSignal: getAbortSignal(),
      } as any;
      console.info("[Stripe Wallet][make-payment] verification requested", {
        hasTransactionId: Boolean(payload.transaction_id),
        intentType: payload.transaction_id?.split("_")?.[0] || "unknown",
        isRecurring: payload.isrecurring,
        paymentMethod: payload.x_payment_method,
        totalPayments: payload.total_payments || 0,
      });
      const { __abortSignal: verifySignal, ...verifyPayload } = payload;
      const verifyStartedAt = Date.now();
      applePayDebug("info", "API request: verify-cashapp-payment", {
        method: "POST",
        url: `${BASE_URL}${VERIFY_WALLET_PAYMENT}`,
        tab,
        signalAlreadyAborted: Boolean(verifySignal?.aborted),
        payload: verifyPayload,
      });
      let res: any = await dispatch(verifyWalletPay(payload));
      const verifyBody = res?.payload?.body;
      applePayDebug(
        verifyBody?.success ? "info" : "error",
        "API response: verify-cashapp-payment",
        {
          durationMs: Date.now() - verifyStartedAt,
          httpStatus: res?.payload?.status,
          success: Boolean(verifyBody?.success),
          thunkRejected: res?.meta?.requestStatus === "rejected",
          rejectedWithValue: Boolean(res?.meta?.rejectedWithValue),
          transportError: res?.error?.message,
          serverMessage: res?.payload?.message,
          message: verifyBody?.data?.msg || verifyBody?.msg,
          body: verifyBody ?? res?.payload ?? null,
        }
      );
      if (res?.payload?.body?.success && success) {
        verificationCompleted = true;
        const { schedule_id, invoice_id } = res?.payload?.body?.data;
        setPaymentProcess("true");
        setEmail(res?.payload?.body?.data?.email);
        setAmount(res?.payload?.body?.data?.total_amount);
        let isRealTime = true;
        if (
          res?.payload?.body?.data?.date &&
          !moment(res?.payload?.body?.data?.date).isSame(moment(), "days")
        ) {
          isRealTime = false;
          setScheduleDate(res?.payload?.body?.data?.date);
        }
        setInvoiceId(tab === "one_time" ? invoice_id : schedule_id);
        if (tab !== "one_time") {
          setScheduleId(schedule_id);
        }
        setOrderId(invoice_id);

        if (tab === "one_time") {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: data?.email,
              event_status: "succeeded",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: res?.payload?.body?.data?.amount,
              order_id: invoice_id,
            })
          );
        } else {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "custom_plan",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: data?.email,
              event_status: "created",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount:
                res?.payload?.body?.data?.totalTransactionAmount ||
                res?.payload?.body?.data?.amount,
              sch_id: schedule_id,
            })
          );
          if (isRealTime) {
            await dispatch(
              addNotification({
                initiated_by: "contact",
                event_type: "payment_processed",
                userid: userData?.data?.accountData?.leadData?.user_id,
                email: data?.email,
                event_status: "succeeded",
                activity_type: "payment_activity",
                clusterid: clusterId,
                communication_status: "",
                accounts_id: userData?.data?.accountData?.id,
                payment_amount: res?.payload?.body?.data?.amount,
                order_id: invoice_id,
              })
            );
          }
        }
        let pay = {
          invoice_id,
          isRealTime,
          schedule_id,
          emails: [res?.payload?.body?.data?.email],
          isFuture: tab !== "one_time",
        };
        await sendEmail(pay);
      } else {
        applePayDebug("error", "verification rejected, showing failed screen", {
          reason: res?.payload?.body?.data?.msg || "no message from server",
          buttonReportedSuccess: success,
        });
        setFailedReason(res?.payload?.body?.data?.msg);
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "failed",
            activity_type: "payment_activity",
            clusterid: clusterId,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: res?.payload?.body?.data?.amount,
            order_id: res?.payload?.body?.data?.invoice_id,
          })
        );
        setAmount(res?.payload?.body?.data?.total_amount);
      }
      setHideButtons(true);
      setIsScreenLoading(false);
    } catch (error: any) {
      applePayDebug("error", "verification threw, showing failed screen", {
        name: error?.name,
        message: error?.message,
        code: error?.code,
        aborted:
          error?.name === "CanceledError" || error?.code === "ERR_CANCELED",
        httpStatus: error?.response?.status,
        responseBody: error?.response?.data,
      });
      setFailedReason("Payment failed");
      setHideButtons(true);
      setPaymentProcess("false");
      setIsScreenLoading(false);
      console.log("handleWalletPay error =====", error);
    } finally {
      if (!verificationCompleted) {
        walletVerificationInFlightRef.current.delete(verificationKey);
      }
      await dispatch(onSetShowDisclosure(false));
      await dispatch(onSetFinalDisclosureLoading(false));
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const handleApplePay = async (
    success: boolean,
    paymentIntent: any,
    failureMessage?: string
  ) => {
    const intentId = paymentIntent?.id;
    const isValidOneTimeIntent =
      tab !== "one_time" ||
      (typeof intentId === "string" && intentId.startsWith("pi_"));

    if (!success || !intentId || !isValidOneTimeIntent) {
      console.error("[Stripe Apple Pay][make-payment] invalid confirmation", {
        hasIntent: Boolean(intentId),
        intentType: intentId?.split("_")?.[0] || "unknown",
        success,
      });
      applePayDebug("error", "page handler rejected confirmation", {
        success,
        intentId,
        tab,
        failureMessage,
      });
      setFailedReason(failureMessage || "Payment failed");
      setPaymentProcess("false");
      dispatch(setIsPaymentProcessing(false));
      return;
    }

    console.info("[Stripe Apple Pay][make-payment] intent accepted", {
      intentType: intentId.split("_")[0],
      paymentMethod: "AP",
    });
    await processStripeWalletPayment(true, paymentIntent, "AP");
  };

  const handleGooglePay = async (paymentIntent: any, totalAmount: any) => {
    if (paymentIntent?.tokenizationData) {
      await handleLegacyGooglePay(paymentIntent, totalAmount);
      return;
    }

    if (!paymentIntent?.id) {
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      dispatch(setIsPaymentProcessing(false));
      return;
    }

    setIsGoogleLoading(true);
    try {
      console.info("[Stripe Google Pay][make-payment] intent accepted", {
        intentType: paymentIntent.id.split("_")[0],
        paymentMethod: "GP",
      });
      await processStripeWalletPayment(true, paymentIntent, "GP");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  //cash app
  const createPayloadPay = (paymentMethod: string) => {
    let amt = searchParams.get("amount");
    let isRecurring = searchParams.get("isRecurring");
    const callbackOfferLinkage = resolveOfferLinkage(
      searchParams.get("offer_type"),
      searchParams.get("vdc_id")
    );
    const effectiveOfferLinkage = callbackOfferLinkage.offer_type
      ? callbackOfferLinkage
      : offerLinkage;
    if (isRecurring === "false") {
      setTab("one_time");
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: accountData?.clusterid,
        isrecurring: "N",
        x_payment_method: paymentMethod,
        amount: parseFloat(amt as string),
        accountid: accountData?.accountid || "",
        first_name: accountData?.first_name || "",
        last_name: accountData?.last_name || "",
        phone_number: accountData.phone,
        module: "process_payment",
        transaction_id: searchParams.get("payment_intent"),
        conv_fee: conFee,
        description: "Online Payment",
        payment_note: "One-time",
        custom_conv: "on",
        ...callbackOfferLinkage,
      };
      return payload;
    } else {
      setTab("schedule");
      console.log("2 ======", 2);
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: accountData?.clusterid,
        isrecurring: "Y",
        ...effectiveOfferLinkage,
        x_payment_method: paymentMethod,
        phone_number: accountData.phone,
        amount:
          paymentPlan?.transaction_type === "amount"
            ? paymentPlan?.transactions?.reduce(
                (accumulator: any, currentValue: any) =>
                  accumulator + parseFloat(currentValue?.amount),
                0
              )
            : paymentPlan?.percentage_amounts?.reduce(
                (accumulator: any, currentValue: any) =>
                  accumulator + currentValue,
                0
              ),
        transaction_id: searchParams.has("payment_intent")
          ? searchParams.get("payment_intent")
          : searchParams.get("setup_intent"),
        first_name: accountData?.first_name || "",
        last_name: accountData?.last_name || "",
        accountid: accountData?.accountid || "",
        module: "process_payment",
        amountfield:
          paymentPlan?.transactions?.map((item: any) => item.amount) || [],
        datefield:
          paymentPlan?.transactions?.map((item: any) =>
            moment(
              item?.date || item?.payment_date || item?.billing_date
            ).format("MM/DD/YYYY")
          ) || [],
        percentagefield:
          paymentPlan?.transactions?.map((item: any) => item.amount) || [],
        exclude_holidays: "N",
        email: accountData?.email || "",
        schedule: paymentPlan?.schedule_fld,
        schedule_type: paymentPlan?.schedule_amount,
        total_payments: paymentPlan?.transactions?.length,
        lead_owner: userData?.data?.accountData?.lead_owner,
        agent: userData?.data?.accountData?.lead_owner,
        start_date: paymentPlan?.start_date,
        amount_type: paymentPlan?.transaction_type,
        conv_fee: conFee,
        description: "Online Payment",
        payment_note: "Custom Plan",
        custom_conv: "on",
      };
      return payload;
    }
  };

  const verifyPay = async (paymentMethod: string, tab: string) => {
    try {
      dispatch(setIsPaymentProcessing(true));
      let payload = {
        ...createPayloadPay(paymentMethod),
        __abortSignal: getAbortSignal(),
      } as any;
      let res: any = await dispatch(verifyWalletPay(payload)).unwrap();
      if (res?.body?.success) {
        const { schedule_id, invoice_id } = res?.body?.data;
        setAmount(res?.body?.data?.total_amount);
        setEmail(res?.body?.data?.email);
        setPaymentProcess("true");
        let isRealTime = true;
        setInvoiceId(invoice_id);
        if (
          res?.body?.data?.date &&
          !moment(res?.body?.data?.date).isSame(moment(), "days")
        ) {
          isRealTime = false;
          setInvoiceId(schedule_id);
        }
        setScheduleDate(res?.body?.data?.date);
        setOrderId(invoice_id);
        if (tab !== "one_time") {
          setScheduleId(schedule_id);
        }

        if (!res?.body?.data?.isDuplicate) {
          if (tab === "one_time") {
            await dispatch(
              addNotification({
                initiated_by: "contact",
                event_type: "payment_processed",
                userid: userData?.data?.accountData?.leadData?.user_id,
                email: res?.body?.data?.email,
                event_status: "succeeded",
                activity_type: "payment_activity",
                clusterid: clusterId,
                communication_status: "",
                accounts_id: userData?.data?.accountData?.id,
                payment_amount: res?.body?.data?.amount,
                order_id: invoice_id,
              })
            );
          } else {
            await dispatch(
              addNotification({
                initiated_by: "contact",
                event_type: "custom_plan",
                userid: userData?.data?.accountData?.leadData?.user_id,
                email: res?.body?.data?.email,
                event_status: "created",
                activity_type: "payment_activity",
                clusterid: clusterId,
                communication_status: "",
                accounts_id: userData?.data?.accountData?.id,
                payment_amount:
                  res?.body?.data?.totalTransactionAmount ||
                  res?.body?.data?.amount,
                sch_id: schedule_id,
              })
            );

            if (isRealTime) {
              await dispatch(
                addNotification({
                  initiated_by: "contact",
                  event_type: "payment_processed",
                  userid: userData?.data?.accountData?.leadData?.user_id,
                  email: res?.body?.data?.email,
                  event_status: "succeeded",
                  activity_type: "payment_activity",
                  clusterid: clusterId,
                  communication_status: "",
                  accounts_id: userData?.data?.accountData?.id,
                  payment_amount: res?.body?.data?.amount,
                  order_id: invoice_id,
                })
              );
            }
          }
          let pay = {
            isRealTime,
            invoice_id,
            schedule_id,
            emails: [res?.body?.data?.email],
            isFuture: tab !== "one_time",
          };
          await sendEmail(pay);
        }
      } else {
        setFailedReason(res?.body?.data?.msg || res?.body?.data?.message);
        setPaymentProcess("false");
        setScheduleDate(res?.body?.data?.date);
        let payment_amount =
          paymentPlan?.transaction_type === "amount"
            ? paymentPlan?.transactions?.[0]?.amount
            : paymentPlan?.percentage_amounts?.[0];
        if (res?.body?.data?.invoice_id) {
          setInvoiceId(res?.body?.data?.invoice_id);
          setOrderId(res?.body?.data?.invoice_id);

          let pay = {
            invoice_id: res?.body?.data?.invoice_id,
            emails: [res?.body?.data?.email],
            isRealTime,
            isFuture: tab !== "one_time",
          };
          await sendEmail(pay);

          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "failed",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: res?.body?.data?.amount ?? payment_amount ?? 0,
              order_id: res?.body?.data?.invoice_id,
            })
          );
        }

        if (
          res?.body?.data?.date &&
          moment(res?.body?.data?.date).isSame(moment(), "days")
        ) {
          setIsRealTime(true);
        }
        setAmount(res?.body?.data?.amount ?? payment_amount ?? 0);
      }
      setHideButtons(true);
    } catch (error: any) {
      setIsScreenLoading(false);
      setFailedReason("Payment failed");
      setHideButtons(true);
      setPaymentProcess("false");
      console.log("verifyPay error =====", error);
    } finally {
      setIsScreenLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetShowDisclosure(false));
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(setIsPaymentProcessing(false));
    }
  };

  //pay pal
  const handleOneTimePayment = async (data: any) => {
    try {
      dispatch(setIsPaymentProcessing(true));
      let payload = {
        payerId: data.payerID,
        paymentId: data.paymentId,
        amount: payPalOrder.amount,
        order_id: payPalOrder.orderId,
        cluster_id: accountData?.clusterid,
        accountid: accountData?.accountid,
        description: "Online Payment",
        payment_note: "One-time",
        ...resolveOfferLinkage(
          searchParams.get("offer_type") || payPalOrder?.offer_type,
          searchParams.get("vdc_id") || payPalOrder?.vdc_id
        ),
        __abortSignal: getAbortSignal(),
      };
      let res: any = await dispatch(payPalOneTimeExecute(payload)).unwrap();

      if (res?.body?.success) {
        setAmount(payPalOrder.amount);
        setEmail(userData?.data?.accountData?.email);
        setPaymentProcess("true");
        setInvoiceId(payPalOrder.orderId);
        setOrderId(payPalOrder.orderId);
        let pay = {
          invoice_id: payPalOrder.orderId,
          emails: [userData?.data?.accountData?.email],
          isRealTime: true,
        };
        await sendEmail(pay);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: userData?.data?.accountData?.email,
            event_status: "succeeded",
            activity_type: "payment_activity",
            clusterid: clusterId,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: payPalOrder.amount,
            order_id: payPalOrder.orderId,
          })
        );
      } else {
        setFailedReason(res?.body?.data?.message);
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "failed",
            activity_type: "payment_activity",
            clusterid: clusterId,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: payPalOrder.amount,
            order_id: payPalOrder.orderId,
          })
        );
      }
      setAmount(payPalOrder.amount);
      setHideButtons(true);
      setIsScreenLoading(false);
    } catch (error: any) {
      setFailedReason("Payment failed");
      setHideButtons(true);
      setPaymentProcess("false");
      console.log("handleApprove error =====", error);
    } finally {
      // setTimeout(() => {
      //   window.history.replaceState(
      //     {},
      //     document.title,
      //     window.location.pathname
      //   );
      //   navigate({ search: "" });
      // }, 500);
      setIsScreenLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetShowDisclosure(false));
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const onGetCustomerDetails = async () => {
    try {
      let params: any = {
        accounts_id: userData?.data?.accountData?.id,
      };
      let res: any = await dispatch(customerDetailGet(params));
      let data = {
        ...userData?.data?.accountData,
        ...res?.payload?.body?.data,
        balance: res?.payload?.body?.data?.balance,
      };
      await dispatch(updateUserData(data));
    } catch (error: any) {
      console.log("onGetCustomerDetails error =====", error);
    }
  };

  useEffect(() => {
    onGetCustomerDetails();

    if (paymentProcess) {
      dispatch(setIsPaymentProcessing(false));
    }
  }, [paymentProcess]);

  const handleSchedulePayPalPayment = async (pay: any) => {
    try {
      dispatch(setIsPaymentProcessing(true));
      const payload = {
        ...pay,
        ...resolveOfferLinkage(
          searchParams.get("offer_type") || payPalOrder?.offer_type,
          searchParams.get("vdc_id") || payPalOrder?.vdc_id
        ),
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
        __abortSignal: getAbortSignal(),
      };
      let res: any = await dispatch(makePayment(payload)).unwrap();
      if (res?.body?.success) {
        let { data } = res?.body;
        setPaymentProcess("true");
        setScheduleDate(data?.date);
        setAmount(data?.total_amount);
        setEmail(data?.email);

        const { schedule_id, invoice_id } = data;
        let isRealTime = true;
        if (data?.date && !moment(data?.date).isSame(moment(), "days")) {
          isRealTime = false;
          setScheduleDate(data?.date);
        }
        setInvoiceId(schedule_id);
        setScheduleId(schedule_id);
        setOrderId(invoice_id);
        setPaymentProcess("true");

        let pay = {
          invoice_id,
          isRealTime,
          schedule_id,
          isFuture: true,
          emails: [data?.email],
        };
        await sendEmail(pay);
        dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "custom_plan",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: data?.email,
            event_status: "created",
            activity_type: "payment_activity",
            clusterid: clusterId,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: data?.totalTransactionAmount || data?.total_amount,
            sch_id: schedule_id,
          })
        );

        if (isRealTime) {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: res?.body?.data?.email,
              event_status: "succeeded",
              activity_type: "payment_activity",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: res?.body?.data?.amount,
              order_id: invoice_id,
            })
          );
        }
        setIsScreenLoading(false);
      } else {
        let payment_amount =
          paymentPlan?.transaction_type === "amount"
            ? paymentPlan?.transactions?.[0]?.amount
            : paymentPlan?.percentage_amounts?.[0];
        if (
          paymentPlan?.transactions?.[0]?.date &&
          moment(paymentPlan?.transactions?.[0]?.date).isSame(moment(), "days")
        ) {
          setScheduleDate(paymentPlan?.transactions?.[0]?.date);
          setIsRealTime(true);
          setAmount(payment_amount);
        }
        dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "succeeded",
            activity_type: "payment_activity",
            clusterid: clusterId,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: payment_amount,
            order_id: payPalOrder.orderId,
          })
        );
        setPaymentProcess("false");
        setFailedReason(res?.body?.data?.msg || res?.body?.data?.message);
        setIsScreenLoading(false);
      }
    } catch (error) {
      setFailedReason("Payment failed");
      setHideButtons(true);
      setPaymentProcess("false");
      setIsScreenLoading(false);
      console.log("handleSchedulePayPalPayment error =====", error);
    } finally {
      dispatch(setIsPaymentProcessing(false));
    }
  };

  // pay pal
  const handleApprove = async (payload: any) => {
    if (payload?.tab === "one_time") {
      handleOneTimePayment(payload);
    }
  };

  const checkPastAmount = async (method?: string) => {
    try {
      let payload = {
        accounts_id: userData?.data?.accountData?.id,
        payment_method: method
          ? method
          : getMethod(isAddNewCard, dataPayment, selectedMethod),
        amount: getAmount(),
        __abortSignal: getAbortSignal(),
      };
      let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
      const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
      if (isDuplicateOrders && duplicateOrdersCount > 0) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const handleCheckDisclosureAndDuplicate = async ({
    method,
    hide = false,
  }: {
    method?: string;
    hide?: boolean;
  }) => {
    try {
      let moveAhead = !checkedDuplicatePayment
        ? await checkPastAmount(method)
        : true;
      if (!moveAhead) {
        setIsLoading(false);
        dispatch(onSetFinalDisclosureLoading(false));
        return false;
      } else {
        if (
          (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
            portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT) &&
          !showFinalDisclosure
        ) {
          setIsLoading(false);
          dispatch(onSetFinalDisclosureLoading(false));
          dispatch(resetDuplicateOrder());
          dispatch(onSetShowDisclosure(true));
          if (hide) {
            setHideButtons(true);
          }
          return false;
        } else {
          dispatch(resetDuplicateOrder());
          dispatch(resetCheckDuplicatePayment());
          return true;
        }
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const createPayPalIntent = async () => {
    let negativeBalance = !checkAmount
      ? await checkOverPaymentAmount({
          amount: tab === "one_time" ? Number(editVal) : getTodayAmount(),
          balance: userData?.data?.accountData?.balance,
          overpayment_protection: portalSettings?.overpayment_protection,
          dispatch,
        })
      : true;
    if (!negativeBalance) {
      return;
    }

    dispatch(updateCheckAmountState(false));
    if (tab === "one_time") {
      try {
        let payload;
        payload = {
          lead_id: accountLeadData?.lead_id,
          first_name: userData?.data?.accountData?.first_name,
          last_name: userData?.data?.accountData?.last_name,
          phone_number: userData?.data?.accountData?.phone,
          amount: parseFloat(editVal),
          agent: userData?.data?.accountData?.lead_owner,
          lead_owner: userData?.data?.accountData?.lead_owner,
          clusterid: clusterId,
          accountid: userData.data.accountData.accountid,
          module: "process_payment",
          success_url: buildPayPalOneTimeReturnUrl(
            window.location.href,
            "success",
            editVal,
            offerLinkage
          ),
          cancel_url: buildPayPalOneTimeReturnUrl(
            window.location.href,
            "cancel",
            editVal,
            offerLinkage
          ),
          is_recurring: "N",
          start_date: moment().format("MM/DD/YYYY"),
          ...offerLinkage,
          __abortSignal: getAbortSignal(),
        };
        let res: any = await dispatch(payPalOneTimeCreate(payload));
        if (res?.payload?.body?.success) {
          await dispatch(
            storePayPalOrder({
              url: res?.payload?.body?.result?.url,
              orderId: res?.payload?.body?.result?.invoice_id,
              amount: editVal,
              ...offerLinkage,
            })
          );
          let moveAhead = await handleCheckDisclosureAndDuplicate({
            method: "PP",
          });
          if (!moveAhead) {
            dispatch(setIsPaymentProcessing(false));
            return res;
          }

          // Reset payment processing immediately before redirect to PayPal
          dispatch(setIsPaymentProcessing(false));
          setTimeout(() => {
            window.location.href = res?.payload?.body?.result?.url;
          }, 0);
        }
        return res;
      } catch (error) {
        console.log("createPayPalIntent error =====", error);
      }
    } else {
      try {
        const payload = {
          is_recurring: "Y",
          phone_number: userData?.data?.accountData?.phone,
          first_name: userData?.data?.accountData?.first_name,
          last_name: userData?.data?.accountData?.last_name,
          accountid: userData?.data?.accountData?.accountid,
          amount: getAmount(),
          agent: userData?.data?.accountData?.lead_owner,
          lead_owner: userData?.data?.accountData?.lead_owner,
          lead_id: accountLeadData?.lead_id,
          clusterid: clusterId,
          success_url: `${
            window.location.href
          }?pay=schedule&method=paypal&amount=${amount}&status=success&isRecurring=${true}${
            offerLinkage.offer_type && offerLinkage.vdc_id
              ? `&offer_type=${encodeURIComponent(offerLinkage.offer_type)}&vdc_id=${offerLinkage.vdc_id}`
              : ""
          }`,
          cancel_url: `${
            window.location.href
          }?pay=schedule&method=paypal&amount=${amount}&status=cancel&isRecurring=${true}${
            offerLinkage.offer_type && offerLinkage.vdc_id
              ? `&offer_type=${encodeURIComponent(offerLinkage.offer_type)}&vdc_id=${offerLinkage.vdc_id}`
              : ""
          }`,
          start_date: new Date(),
          __abortSignal: getAbortSignal(),
        };
        let res: any = await dispatch(createPaypalToken(payload));
        if (res?.payload?.body?.success) {
          await dispatch(
            storePayPalOrder({
              url: res?.payload?.body?.result?.url,
              orderId: res?.payload?.body?.result?.invoice_id,
              token: res?.payload?.body?.result?.token,
              ...offerLinkage,
            })
          );
          //check duplicate payment and disclosure
          let moveAhead = await handleCheckDisclosureAndDuplicate({
            method: "PP",
            hide: true,
          });
          if (!moveAhead) {
            dispatch(setIsPaymentProcessing(false));
            await dispatch(storePaymnetPlan(data?.paymentPlan));
            return;
          }
          dispatch(updateCheckAmountState(false));
          dispatch(storePaymnetPlan(data?.paymentPlan));
          // Reset payment processing immediately before redirect to PayPal
          dispatch(setIsPaymentProcessing(false));
          setTimeout(() => {
            window.location.href = res?.payload?.body?.result?.url;
          }, 0);
        } else {
          setFailedReason(res?.payload?.body?.data?.msg);
        }
      } catch (error) {
        console.log("createPayPalIntent error =====", error);
      }
    }
  };

  const setRecurringAmount = () => {
    setAmount(paymentPlan?.transactions?.[0]?.amount);
  };

  const handlePayPalSchedulePayment = async (tokendata: any) => {
    try {
      dispatch(setIsPaymentProcessing(true));
      const payload = { ...tokendata, __abortSignal: getAbortSignal() };
      let res: any = await dispatch(createPaypalPaymentToken(payload)).unwrap();
      if (res?.body?.success) {
        await handleSchedulePayPalPayment({
          cluster_id: clusterId,
          x_email: userData?.data?.accountData?.email,
          x_payment_method: "PP",
          cc_token: res?.body?.data?.cc_token,
          cppid: res?.body?.data?.cppid,
          accountid: userData?.data?.accountData?.accountid,
          lead_id: accountLeadData?.lead_id,
          city: "",
          address1: "",
          address2: "",
          zipcode: "",
          state: "",
          country: "",
          first_name: userData?.data?.accountData?.first_name,
          last_name: userData?.data?.accountData?.last_name,
          amount:
            paymentPlan?.transaction_type === "amount"
              ? paymentPlan?.transactions?.reduce(
                  (accumulator: any, currentValue: any) =>
                    accumulator + parseFloat(currentValue?.amount),
                  0
                )
              : paymentPlan?.percentage_amounts?.reduce(
                  (accumulator: any, currentValue: any) =>
                    accumulator + currentValue,
                  0
                ),
          use_old_card: !isAddNewCard ? "0" : "1",
          description: "Online Payment",
          payment_note: "Custom Plan",
          customer_device: getOperatingSystem(),
          card_type: "",
          old_cardnumber: "",
          old_cvv: "",
          old_expiry: "",
          old_4digit: "",
          old_type: "",
          old_zip: "",
          cvv: "",
          cexpiry: "",
          ccardnumber: "",
          choldername: "",
          aholdername: "",
          routing_number: "",
          account_number: "",
          bank_name: "",
          bank_address: "",
          isrecurring: "Y",
          stripe_token: "",
          phone_number: accountData.phone,
          agent: userData?.data?.accountData?.lead_owner,
          lead_owner: userData?.data?.accountData?.lead_owner,
          transaction_id: searchParams.has("payment_intent")
            ? searchParams.get("payment_intent")
            : searchParams.get("setup_intent"),
          is3ds: false,
          amountfield:
            paymentPlan?.transactions?.map((item: any) => item.amount) || [],
          datefield:
            paymentPlan?.transactions?.map((item: any) =>
              moment(
                item?.date || item?.payment_date || item?.billing_date
              ).format("MM/DD/YYYY")
            ) || [],
          percentagefield:
            paymentPlan?.transactions?.map((item: any) => item.amount) || [],
          exclude_holidays: "N",
          email: accountData?.email || "",
          schedule: paymentPlan?.schedule_fld,
          schedule_type: paymentPlan?.schedule_amount,
          total_payments: paymentPlan?.transactions?.length,
          max_transaction: paymentPlan?.transactions?.length,
          start_date: paymentPlan?.start_date,
          amount_type: paymentPlan?.transaction_type,
        });
      } else {
        setPaymentProcess("false");
        setFailedReason(
          res?.body?.data?.msg || res?.body?.data?.message || "Payment Failed"
        );
        if (
          paymentPlan?.transactions?.[0]?.date &&
          moment(paymentPlan?.transactions?.[0]?.date).isSame(moment(), "days")
        ) {
          setIsRealTime(true);
          setAmount(
            paymentPlan?.transaction_type === "amount"
              ? paymentPlan?.transactions?.[0]?.amount
              : paymentPlan?.percentage_amounts?.[0]
          );
        }
        setIsScreenLoading(false);
      }
    } catch (error) {
      setPaymentProcess("false");
      setFailedReason("Payment failed");
      setIsScreenLoading(false);
      console.log("handlePayPalSchedulePayment error =====", error);
    } finally {
      // setTimeout(() => {
      //   window.history.replaceState(
      //     {},
      //     document.title,
      //     window.location.pathname
      //   );
      //   navigate({ search: "" });
      // }, 500);
      setIsScreenLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetShowDisclosure(false));
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(setIsPaymentProcessing(false));
    }
  };

  useEffect(() => {
    const callbackFlow = resolvePaymentCallbackFlow(searchParams);

    if (searchParams.has("amount") && searchParams.get("amount")) {
      setIsScreenLoading(true);
      if (callbackFlow === "one_time") {
        setTab("one_time");
        setEditVal(searchParams.get("amount") ?? "0.00");
        setAmount(searchParams.get("amount") ?? "0.00");
      } else {
        setTab("schedule");
        setRecurringAmount();
      }
    }
    //cash app

    if (
      (searchParams.has("payment_intent") ||
        searchParams.has("setup_intent")) &&
      !searchParams.has("checked") &&
      searchParams.has("method") &&
      searchParams.has("amount")
    ) {
      setIsScreenLoading(true);
      setInitialDisclosureAccept(true);
      searchParams.set("checked", "true");
      verifyPay(
        searchParams.get("method") === "cashapp"
          ? "CAPP"
          : searchParams.get("method") === "bank"
            ? "ACH"
            : "AMZP",
        searchParams.get("pay") ?? "one_time"
      );
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }

    //pay pal
    if (searchParams.get("pay") === "one_time") {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      if (searchParams.has("status")) {
        setInitialDisclosureAccept(true);
        setIsScreenLoading(true);
        setAmount(searchParams.get("amount") ?? "0");
        if (
          (searchParams.has("PayerID") || searchParams.has("paymentId")) &&
          searchParams.has("checked") &&
          searchParams.get("checked") === "false" &&
          searchParams.has("pay") &&
          searchParams.has("token") &&
          searchParams.get("status") === "success"
        ) {
          searchParams.set("checked", "true");
          handleApprove({
            tab: searchParams.get("pay"),
            paymentId: searchParams.get("paymentId"),
            payerID: searchParams.get("PayerID"),
            token: searchParams.get("token"),
          });
        } else if (!isScreenLoading) {
          searchParams.set("checked", "true");
          setIsScreenLoading(false);
          dispatch(onSetShowDisclosure(false));
          dispatch(onSetFinalDisclosureLoading(false));
          setFailedReason("Payment canceled");
          setPaymentProcess("false");
        }
        setTimeout(() => {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          navigate({ search: "" });
        }, 500);
      }
    } else if (searchParams.get("pay") === "schedule") {
      if (searchParams.has("status")) {
        setIsScreenLoading(true);
        setInitialDisclosureAccept(true);
        dispatch(resetDuplicateOrder());
        dispatch(resetCheckDuplicatePayment());
        setTab("schedule");
        setEditVal(payPalOrder.amount);
        let payment_amount =
          paymentPlan?.transaction_type === "amount"
            ? paymentPlan?.transactions?.[0]?.amount
            : paymentPlan?.percentage_amounts?.[0];
        setAmount(payment_amount);
        if (
          (searchParams.has("approval_session_id") ||
            searchParams.has("approval_token_id")) &&
          !searchParams.has("checked") &&
          searchParams.get("status") === "success" &&
          searchParams.has("amount") &&
          searchParams.get("method") === "paypal"
        ) {
          searchParams.set("checked", "true");

          const tokendata = {
            token: payPalOrder?.token,
          };
          handlePayPalSchedulePayment(tokendata);
        } else {
          if (
            paymentPlan?.transactions?.[0]?.date &&
            moment(paymentPlan?.transactions?.[0]?.date).isSame(
              moment(),
              "days"
            )
          ) {
            setIsRealTime(true);
          }

          setPaymentProcess("false");
          setFailedReason("Payment canceled");
          setIsScreenLoading(false);
          dispatch(onSetShowDisclosure(false));
          dispatch(onSetFinalDisclosureLoading(false));
          setTimeout(() => {
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname
            );
            navigate({ search: "" });
          }, 500);
          setIsScreenLoading(false);
        }
      }
    }

    dispatch(getHolidayList({ date: moment().format("MM/DD/YYYY") }));
  }, []);

  const setDataForSelectedOffer = (offer: any) => {
    if (offer) {
      const transactionLimitError = getNonBudgetOfferTransactionLimitError(
        offer,
        portalSettings?.transaction_limit
      );
      if (transactionLimitError) return;

      setIsEndDateError(false);
      setDateErrMsg(null);
      setScheduleAmountErr(null);

      if (offer.offer_type === "BC") {
        setEditVal(
          (
            accountData?.balance /
            Math.ceil(accountData?.balance / budgerCalculatorIncome)
          ).toFixed(2)
        );
        const tmp = { ...data.paymentPlan };
        tmp.schedule_amount = "auto";
        tmp.no_of_transaction =
          budgerCalculatorIncome === 0
            ? 1
            : Math.ceil(accountData?.balance / budgerCalculatorIncome);
        tmp.amount =
          accountData?.balance /
          Math.ceil(accountData?.balance / budgerCalculatorIncome);
        tmp.amount_type = "amount";
        tmp.schedule_fld = "monthly";
        setData({ ...data, paymentPlan: tmp });
        requestScheduleRefresh(true);
      } else {
        if (offer?.frequency_type === "R") {
          setEditVal(calculateTotal(accountData?.balance, offer).toFixed(2));
        } else {
          const tmp = { ...data.paymentPlan };
          tmp.schedule_amount = "auto";
          tmp.no_of_transaction = offer?.no_of_transactions;
          tmp.amount =
            calculateTotal(accountData?.balance, offer) /
            (offer?.no_of_transactions || 1);
          tmp.amount_type = "amount";
          tmp.schedule_fld = offer?.payment_frequency || "daily";
          setData({ ...data, paymentPlan: tmp });
          requestScheduleRefresh(true);
        }
      }
    }
  };

  useEffect(() => {
    if (
      !isEdit &&
      !availableOffer &&
      !selectedOffer &&
      tab === "one_time" &&
      parseFloat(editVal) > 0.0 &&
      generalSettings
    ) {
      const { min, max, isConfigured } = resolvePaymentAmountLimits(
        generalSettings,
        userData?.data?.accountData?.balance
      );
      if (!isConfigured) {
        setIsEdit(true);
        setAmtErr(
          "Payment limits are unavailable. Please refresh and try again."
        );
        return;
      }
      let str = editVal.replaceAll(",", "");
      if (parseFloat(str) < min || parseFloat(str) > max) {
        setIsEdit(true);
        setAmtErr(
          `Amount should be between ${formatCurrency(min)} and ${formatCurrency(
            max
          )}`
        );
      }
    }
  }, [
    availableOffer,
    editVal,
    generalSettings,
    isEdit,
    selectedOffer,
    tab,
    userData?.data?.accountData?.balance,
  ]);

  // for drawer
  useEffect(() => {
    const updateMaxHeight = () => {
      if (drawerRef.current && !isLoading) {
        drawerRef.current.style.maxHeight = showDetails
          ? `${drawerRef.current.scrollHeight}px`
          : "0";
      }
    };
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => {
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [showDetails, isLoading, activeOffer]);

  const getAmount = () => {
    if (paymentProcess) {
      return amount;
    }

    if (selectedOffer || availableOffer) {
      if (activeOffer === "BC") {
        if (budgerCalculatorIncome <= 0) {
          return 0;
        }
        return (
          accountData?.balance /
          Math.ceil(accountData?.balance / budgerCalculatorIncome)
        );
      } else {
        if (activeOffer !== "BC") {
          return (
            calculateTotal(accountData?.balance, selectedOffer) /
            (selectedOffer?.no_of_transactions || 1)
          );
        } else {
          return accountData?.balance;
        }
      }
    }

    if (tab === "schedule") {
      const scheduleAmount =
        data?.paymentPlan?.schedule_amount === "custom"
          ? data?.paymentPlan?.transaction_type === "percent"
            ? (data?.paymentPlan?.percentage_amounts?.[0] ?? 0)
            : (data?.paymentPlan?.transactions[0]?.amount ?? 0)
          : data?.paymentPlan?.schedule_amount === "auto"
            ? data?.paymentPlan?.transactions[0]?.amount
            : data?.paymentPlan?.amount;
      return scheduleAmount || 0;
    }

    return `${editVal ?? 0.0}`;
  };

  const generalMarkers = getGeneralDetailMarkers({
    conFee,
    paymentAmount: getAmount() ?? "",
    nextDate:
      tab === "one_time"
        ? moment().format("MM/DD/YYYY")
        : moment(data?.paymentPlan?.transactions[0]?.billing_date).format(
            "MM/DD/YYYY"
          ),
  });

  const getTodayAmount = () => {
    let scheduleAmount = 0;

    if (tab === "schedule" && !selectedOffer && !availableOffer) {
      if (data?.paymentPlan?.schedule_amount === "custom") {
        if (data?.paymentPlan?.transaction_type === "percent") {
          scheduleAmount = moment(
            data?.paymentPlan?.transactions[0]?.billing_date
          ).isSame(moment(), "day")
            ? data?.paymentPlan?.percentage_amounts?.[0]
            : 0;
        } else {
          scheduleAmount = moment(
            data?.paymentPlan?.transactions[0]?.billing_date
          ).isSame(moment(), "day")
            ? data?.paymentPlan?.transactions[0]?.amount
            : 0;
        }
      } else if (data?.paymentPlan?.schedule_amount === "auto") {
        scheduleAmount = moment(
          data?.paymentPlan?.transactions[0]?.billing_date
        ).isSame(moment(), "day")
          ? data?.paymentPlan?.transactions[0]?.amount
          : 0;
      } else {
        scheduleAmount = moment(
          data?.paymentPlan?.transactions[0]?.billing_date
        ).isSame(moment(), "day")
          ? data?.paymentPlan?.amount
          : 0;
      }
    }
    return scheduleAmount;
  };

  const scheduleAmountLimitBalance = selectedOffer
    ? calculateTotal(accountData?.balance, selectedOffer)
    : accountData?.balance;
  const hasConfiguredScheduleLimits = resolvePaymentAmountLimits(
    generalSettings,
    scheduleAmountLimitBalance
  ).isConfigured;
  const activeScheduleAmountError =
    amountLimitsStatus === "loading"
      ? "Payment limits are loading. Please wait."
      : amountLimitsStatus === "error" || !hasConfiguredScheduleLimits
        ? "Payment limits are unavailable. Please refresh and try again."
        : scheduleAmountErr;

  // Payment processing navigation guard
  const [showModal, setShowModal] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const getAbortSignal = () => {
    if (
      !abortControllerRef.current ||
      abortControllerRef.current.signal.aborted
    ) {
      abortControllerRef.current = new AbortController();
    }

    return abortControllerRef.current.signal;
  };

  // Get payment processing state from Redux

  const { allowPendingNavigation, cancelPendingNavigation } =
    useNavigationGuard({
      shouldBlock: () => isPaymentProcessing,
      onNavigationAttempt: () => setShowModal(true),
    });

  const handleConfirmLeave = () => {
    // Cancel ongoing payment API requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset payment states
    setIsScreenLoading(false);
    setIsGoogleLoading(false);
    setScheduleCreateLoading(false);
    setIsLoading(false);

    // Reset Redux payment processing state
    dispatch(setIsPaymentProcessing(false));

    // Close modal and allow navigation
    setShowModal(false);
    setTimeout(() => allowPendingNavigation(), 250);
  };

  const handleCancelLeave = () => {
    setShowModal(false);
    cancelPendingNavigation();
  };

  // Prevent page reload during payment processing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isPaymentProcessing) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isPaymentProcessing]);

  const isStax = useMemo(() => {
    if (
      scheduleMethod === "CC" &&
      paymentPermission?.CC_gateway_name?.name === PaymentGateway.STAX
    ) {
      return true;
    } else if (
      scheduleMethod === "ACH" &&
      paymentPermission?.ach_gateway_name?.name === PaymentGateway.STAX
    ) {
      return true;
    }

    return false;
  }, [scheduleMethod, paymentPermission]);

  return (
    <>
      <div
        className={`lg:grid grid-cols-2 lg:h-[var(--shell-vh)] lg:overflow-hidden overflow-y-scroll`}
      >
        <div
          style={{ backgroundColor: brandColor }}
          className="relative lg:h-full lg:max-h-[var(--shell-vh)] lg:overflow-y-scroll lg:py-[90px] py-20 pb-5 md:px-10 px-5"
        >
          <div className="lg:max-w-[380px] w-full mx-auto flex flex-col items-start lg:gap-10 gap-3 ">
            <div className="lg:flex hidden items-center gap-[10px]">
              {isLoading ? (
                <Skeleton width={150} height={52} />
              ) : paymentLogo ? (
                <div className="h-[35px] lg:h-14 flex">
                  <img
                    src={paymentLogo}
                    alt=""
                    className="animate__animated animate__fadeIn object-contain"
                  />
                </div>
              ) : (
                <img
                  src={logo}
                  alt=""
                  className="animate__animated animate__fadeIn"
                />
              )}
              {!paymentLogo && (
                <div className="text-purple font-medium text-xl leading-[30px]">
                  CompanyName
                </div>
              )}
            </div>
            <div className="w-full ">
              <div className="w-full flex justify-between">
                <h1
                  className={`${
                    isDark ? "text-darktxtParagraph" : "text-txtParagraph"
                  } font-bold lg:text-xl text-lg leading-[26.04px] lg:mb-5 mb-3`}
                >
                  Make a Payment
                </h1>
                <span
                  className={`lg:hidden block ${
                    isDark
                      ? "text-darkTxtBlue hover:!text-darkBlueHover"
                      : "text-blue hover:!text-btnPrimaryHover"
                  } underline cursor-pointer`}
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? "Hide details" : "View details"}
                </span>
              </div>

              <OutsideClickHandler
                onOutsideClick={handleOutsideClick}
                className="w-fit"
              >
                <div className="flex items-end gap-3 cursor-pointer">
                  {isEdit && tab === "one_time" ? (
                    <div
                      className="w-[266px] relative"
                      ref={amtInputContainerRef}
                    >
                      <CustomAmountInput
                        checkAmt={false}
                        isCheckAllowed={false}
                        inputValue={editVal}
                        balance={userData?.data?.accountData?.balance}
                        setinputValue={(e: any) => {
                          setAmtErr(null);
                          setEditVal(e);
                          dispatch(setIsPaymentProcessing(true));
                        }}
                        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                          setIsEdit(handleBlur(event.target.value));
                        }}
                        className={`lg:!text-[50px] !text-[32px] lg:!leading-[65.1px] leading-[41.66px] font-bold text-txtPrimary lg:!h-[65px] !h-[42px] lg:!pl-[45px] !pl-[32px] ${
                          amtErr
                            ? "!shadow-[0_0_4px_0px_rgba(255,0,0,1)]"
                            : "!shadow-[0_0_4px_0px_rgba(22,120,242,1)]"
                        }`}
                        _className="lg:!text-[50px] !text-[32px] lg:!leading-[65.1px] leading-[41.66px] font-bold text-txtPrimary !top-0  left-[12px]"
                      />
                      {amtErr && (
                        <p className="pt-4 text-red text-sm leading-[18.23px]">
                          {amtErr}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <p
                        className={`font-bold lg:leading-[65.1px] ${
                          isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                        } lg:text-[50px] text-[32px]`}
                        onClick={() => {
                          if (
                            !availableOffer &&
                            !selectedOffer &&
                            !isLoading &&
                            !isScreenLoading &&
                            !paymentProcess &&
                            !isAssistanceRequested &&
                            !showFinalDisclosure &&
                            tab === "one_time"
                          )
                            setIsEdit(true);
                        }}
                      >
                        {isLoading || isScreenLoading ? (
                          <Skeleton width={150} height={59} />
                        ) : (
                          formatCurrency(getAmount())
                        )}
                      </p>
                      {!availableOffer &&
                        !selectedOffer &&
                        !isLoading &&
                        !isScreenLoading &&
                        !paymentProcess &&
                        !isAssistanceRequested &&
                        !showFinalDisclosure &&
                        tab === "one_time" && (
                          <div
                            className="lg:pb-[15px] pb-3"
                            onClick={() => setIsEdit(true)}
                          >
                            <EditIcon
                              strokeColor={
                                isDark
                                  ? "var(--color-icon-dark-bright)"
                                  : "var(--color-icon-bright)"
                              }
                              className="group "
                              _className={
                                isDark
                                  ? "group-hover:!fill-darkTxtPrimary"
                                  : "group-hover:!fill-iconObscure"
                              }
                            />
                          </div>
                        )}
                    </>
                  )}
                </div>
              </OutsideClickHandler>
              {!isLoading && tab === "schedule" && (
                <p
                  className={`text-sm ${
                    isDark ? "text-darkTxtSecondary" : "text-txtSecondary"
                  }`}
                >
                  1st Installment
                </p>
              )}
            </div>

            {!paymentProcess && !isAssistanceRequested && (
              <div className="w-full">
                <TabSwitch
                  leftText="One-time payment"
                  rightText="Schedule payment"
                  disabledTab={
                    portalSettings?.external_schedule_payment_opt ===
                    SettingsValue.YES
                      ? [""]
                      : ["right"]
                  }
                  selected={tab === "one_time"}
                  selectedText={tab === "one_time" ? "left" : "right"}
                  onClick={(isOneTimeSelected: boolean) => {
                    if (isOneTimeSelected) {
                      console.log("5 ======", 5);
                      setTab("one_time");
                      setActiveOffer("");
                    } else {
                      setTab("schedule");
                      setPaymentProcess("");
                      setSelectedOffer(undefined);
                      dispatch(onSetFinalDisclosureLoading(false));
                    }
                    dispatch(onSetShowDisclosure(false));
                  }}
                  disabled={
                    (availableOffer || selectedOffer ? true : false) ||
                    isFinalDisclousreLoading ||
                    (portalSettings?.state_disclosure_flag === SettingsValue.OFF
                      ? false
                      : !initialDisclosureAccept)
                  }
                />
              </div>
            )}
            <span
              ref={drawerRef}
              className={`w-full flex flex-col gap-5 lg:gap-10 drawer1 ${
                showDetails ? "open h-full" : ""
              }`}
            >
              <div className="w-full border-b border-strokeActive"></div>
              <div className="w-full">
                <p
                  className={`flex justify-between mb-4  ${
                    isDark ? "text-darkTxtBlue" : "text-blue "
                  } `}
                >
                  {(accountData?.first_name || "") +
                    " " +
                    (accountData?.last_name || "")}
                  <span
                    className={`${
                      isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                    }`}
                  >
                    {accountData?.accountid}
                  </span>
                </p>

                <p
                  className={`flex justify-between ${
                    isDark ? "text-darkTxtSecondary" : "text-txtSecondary"
                  } text-sm`}
                >
                  Balance <span>Description</span>
                </p>

                <div className="grid grid-cols-2 w-full">
                  <p
                    className={`leading-[20px] ${
                      isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                    } text-sm text-left`}
                  >
                    {isLoading ? (
                      <Skeleton width={70} />
                    ) : (
                      <>
                        {formatCurrency(userData?.data?.accountData?.balance) ||
                          "$0.00"}
                      </>
                    )}
                  </p>
                  <p
                    className={`leading-[20px] ${
                      isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                    } text-sm text-right`}
                  >
                    {isLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      (userData?.data?.accountData?.description ?? "--")
                    )}
                  </p>
                </div>
              </div>

              {activeOffer && (
                <>
                  <div className="w-full border-b border-strokePrimary"></div>
                  <div className="w-full">
                    {offerStep === 2 && !paymentProcess && (
                      <div
                        className={`w-full pb-5 text-sm ${
                          isDark
                            ? "text-darkTxtBlue hover:!text-darkBlueHover"
                            : "text-blue hover:!text-btnPrimaryHover"
                        } font-medium `}
                      >
                        <span
                          className="flex items-center justify-start gap-1 cursor-pointer"
                          onClick={() => {
                            setActiveOffer(activeOffer === "BC" ? "BC" : "");
                            setSelectedOffer(undefined);
                            setOfferStep(1);
                            setAvailableOffer(true);
                            console.log("6 ======", 6);
                            setTab("one_time");
                            setIsEdit(false);
                            setAmtErr("");
                            setHideButtons(true);
                          }}
                        >
                          <BackArrow /> Go back
                        </span>
                      </div>
                    )}
                    <div className="rounded-2xl border border-strokePrimary bg-bgPrimary w-full">
                      <h1 className="text-sm font-bold p-4 lg:px-5 px-4 text-txtPrimary uppercase">
                        {activeOffer === "BC"
                          ? "Payment plan based on net income"
                          : " OFFER TERMS"}
                      </h1>
                      <div className="flex gap-5 bg-bgThird lg:p-5 py-5 px-4 items-center">
                        <div>
                          <OfferIcon />
                        </div>
                        <div className="grow">
                          <h1 className="text-xl font-bold">
                            {selectedOffer?.headline}
                          </h1>
                          <p className="text-txtParagraph text-sm">
                            {selectedOffer?.description || "--"}
                          </p>
                        </div>
                      </div>
                      <div className="lg:px-5 px-4">
                        <p className="flex justify-between items-center py-4 border-b text-sm text-txtSecondary">
                          <span className="inline-flex items-center gap-2">
                            {tab === "one_time" ? "Payment date" : "Start date"}{" "}
                            <CustomTooltip
                              maxwidth="280px"
                              place="top"
                              id="payment-date-anchor"
                              content={`<div class="text-xs leading-[18px] font-normal">The date when the first payment installment is scheduled to be processed.</div>`}
                            >
                              <InfoIcon
                                id="payment-date-anchor"
                                className="cursor-pointer group"
                                _className="group-hover:!stroke-iconObscure"
                              />
                            </CustomTooltip>
                          </span>
                          <span className="text-txtPrimary font-medium">
                            {formatPaymentListDate(
                              new Date().toISOString(),
                              userData?.data?.accountData?.timezone,
                              "short"
                            )}
                          </span>
                        </p>
                        <p className="flex justify-between items-center py-4 border-b text-sm text-txtSecondary">
                          <span className="inline-flex items-center gap-2">
                            Frequency{" "}
                            <CustomTooltip
                              maxwidth="280px"
                              place="top"
                              id="frequency-anchor"
                              content={`<div class="text-xs leading-[18px] font-normal">Defines how often the installments will be processed.</div>`}
                            >
                              <div
                                id="frequency-anchor"
                                className="cursor-pointer pointer-events-auto"
                              >
                                <InfoIcon />
                              </div>
                            </CustomTooltip>
                          </span>
                          <span className="text-txtPrimary font-medium">
                            {activeOffer !== "BC"
                              ? selectedOffer?.payment_frequency
                                ? selectedOffer?.payment_frequency
                                    .charAt(0)
                                    .toUpperCase() +
                                  selectedOffer?.payment_frequency?.slice(1)
                                : ""
                              : "Monthly"}
                          </span>
                        </p>
                        <p
                          className={`flex justify-between items-center py-4 ${
                            activeOffer !== "BC" && "border-b"
                          } text-sm text-txtSecondary`}
                        >
                          <span className="inline-flex items-center gap-2">
                            Number of transactions{" "}
                            <CustomTooltip
                              maxwidth="280px"
                              place="top"
                              id="transaction-anchor"
                              content={`<div class="text-xs leading-[18px] font-normal">The total number of payment installments within this schedule.</div>`}
                            >
                              <div
                                id="transaction-anchor"
                                className="cursor-pointer pointer-events-auto"
                              >
                                <InfoIcon />
                              </div>
                            </CustomTooltip>
                          </span>
                          <span className="text-txtPrimary font-medium">
                            {activeOffer !== "BC"
                              ? selectedOffer?.no_of_transactions
                              : budgerCalculatorIncome <= 0
                                ? 1
                                : Math.ceil(
                                    accountData?.balance /
                                      budgerCalculatorIncome
                                  )}
                          </span>
                        </p>
                        {activeOffer !== "BC" && (
                          <p className="flex justify-between items-center py-4  text-sm text-txtPrimary font-bold">
                            <span className="inline-flex items-center gap-2">
                              Discount amount{" "}
                              <CustomTooltip
                                maxwidth="280px"
                                place="top"
                                id="discount-payments-anchor"
                                content={`<div class="text-xs leading-[18px] font-normal">Total discounted amount for this offer.</div>`}
                              >
                                <div
                                  id="discount-payments-anchor"
                                  className="cursor-pointer pointer-events-auto"
                                >
                                  <InfoIcon />
                                </div>
                              </CustomTooltip>
                            </span>
                            <span className="text-xl">
                              {selectedOffer?.settlement_offer === "F" && "$"}
                              {selectedOffer?.settlement_amount}
                              {selectedOffer?.settlement_offer === "P" && "%"}
                            </span>
                          </p>
                        )}
                      </div>
                      <p className="flex justify-between items-center border-t text-xl text-txtPrimary font-bold bg-bgThird lg:p-5 p-4 rounded-bl-2xl	rounded-br-2xl	">
                        <span className="inline-flex items-center gap-2">
                          TOTAL{" "}
                          <CustomTooltip
                            maxwidth="280px"
                            place="top"
                            id="total-payments-anchor"
                            content={`<div class="text-xs leading-[18px] font-normal">Total payment amount for this offer.</div>`}
                          >
                            <div
                              id="total-payments-anchor"
                              className="cursor-pointer pointer-events-auto"
                            >
                              <InfoIcon />
                            </div>
                          </CustomTooltip>
                        </span>
                        <span className="text-xl">
                          {formatCurrency(
                            calculateTotal(accountData?.balance, selectedOffer)
                          ) || "$0.00"}
                        </span>
                      </p>
                    </div>
                  </div>
                </>
              )}
            </span>
            <Footer
              style={{ backgroundColor: brandColor }}
              className={`!justify-start ${
                activeOffer ? "!w-full" : "!w-fit"
              } lg:max-w-[380px] lg:flex hidden xl:flex-nowrap flex-wrap ${
                isDark ? "!text-darktxtParagraph" : "!text-txtParagraph"
              }`}
            />
          </div>
        </div>

        {isScreenLoading && (
          <div className="w-full lg:h-[var(--shell-vh)] flex justify-center items-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
          </div>
        )}

        {!isScreenLoading &&
          portalSettings?.state_disclosure_flag !== SettingsValue.OFF &&
          !initialDisclosureAccept && (
            <div className="w-full lg:h-[var(--shell-vh)] lg:overflow-y-auto relative lg:pt-20 lg:pb-[40px] lg:px-20 md:p-10 p-5">
              <DisclosureInitial
                isOpen={
                  !isScreenLoading &&
                  portalSettings?.state_disclosure_flag !== SettingsValue.OFF &&
                  !initialDisclosureAccept
                }
                handleAccept={() => {
                  setInitialDisclosureAccept(true);
                  scrollPaymentContentToTop();
                }}
                generalMarkers={generalMarkers}
                onClose={() => navigate(`/${cluster_id}/dashboard`)}
              />
            </div>
          )}

        {!isScreenLoading &&
          (portalSettings?.state_disclosure_flag === SettingsValue.OFF ||
            initialDisclosureAccept) && (
            <div
              ref={paymentContentRef}
              className={`w-full lg:h-[var(--shell-vh)] h-fit lg:overflow-y-auto relative overflow-x-hidden`}
            >
              <>
                {availableOffer && !paymentProcess && !isAssistanceRequested ? (
                  <AvailableOffer
                    setActiveOffer={setActiveOffer}
                    activeOffer={activeOffer}
                    availableStateLoading={availableStateLoading}
                    availableOfferData={availableOfferData}
                    setSelectedOffer={setSelectedOffer}
                    availableOffer={availableOffer}
                    setAvailableOffer={setAvailableOffer}
                    selectedOffer={selectedOffer}
                    setTab={setTab}
                    setBudgetCalculatorIncome={setBudgetCalculatorIncome}
                    budgerCalculatorIncome={budgerCalculatorIncome}
                    setDataForSelectedOffer={setDataForSelectedOffer}
                    setOfferStep={setOfferStep}
                    handleSendAssistanceRequest={handleSendAssistanceRequest}
                    isAssistanceLoading={isAssistanceLoading}
                  />
                ) : (
                  <>
                    {isAssistanceRequested ? (
                      <div className="h-full w-full grid place-items-center">
                        <AssistanceRequest />
                      </div>
                    ) : (
                      <>
                        {tab === "one_time" ? (
                          paymentProcess ? (
                            paymentProcess === "true" ? (
                              isWirePayment ? (
                                <div className="h-full w-full grid place-items-center">
                                  <WirePaymentSuccess
                                    bankDetails={wireDetails}
                                    orderId={invoiceId}
                                    sendEmail={sendEmail}
                                    lead_id={accountLeadData?.lead_id}
                                    className=""
                                    showSuccessToast
                                  />
                                </div>
                              ) : (
                                <div className="h-full w-full grid place-items-center">
                                  <OneTimeSuccess
                                    email={email}
                                    amount={amount}
                                    invoiceId={invoiceId}
                                    sendEmail={sendEmail}
                                    clusterId={clusterId}
                                    lead_id={accountLeadData?.lead_id}
                                    showSuccessToast
                                  />
                                </div>
                              )
                            ) : (
                              <div className="lg:pt-0 pt-5 w-full lg:h-full">
                                <PaymentLinkProcessFailed
                                  failedReason={failedReason}
                                  handleBackToStart={handleBackToStart}
                                />
                              </div>
                            )
                          ) : (
                            <OneTimePayment
                              setIsGoogleLoading={setIsGoogleLoading}
                              generalMarkers={generalMarkers}
                              selectedMethod={selectedMethod}
                              setSelectedMethod={setSelectedMethod}
                              currentStep={2}
                              setPaymentProcess={setPaymentProcess}
                              sendEmail={sendEmail}
                              setAmount={setAmount}
                              setFailedReason={setFailedReason}
                              handleNext={async (values: any) => {
                                if (values.x_payment_method === "WTO") {
                                  await paymentViaWire();
                                } else {
                                  await processPaymentLink(values);
                                }
                              }}
                              editVal={editVal}
                              isPaymentLink={true}
                              values={values}
                              setValues={setValues}
                              createNewCard={(values: any) =>
                                handleCreateNewCard(values)
                              }
                              dataPayment={dataPayment}
                              setDataPayment={setDataPayment}
                              setCurrentStep={setCurrentStep}
                              isDataLoading={isDataLoading}
                              setSavedCardsEmail={setSavedCardsEmail}
                              offerAmount={offerAmount?.toString()}
                              offerType={offerLinkage.offer_type}
                              vdcId={offerLinkage.vdc_id}
                              isEdit={isEdit}
                              handleGooglePay={handleGooglePay}
                              createPayPalIntent={createPayPalIntent}
                              isGoogleLoading={isGoogleLoading}
                              paymentMethodList={paymentMethodList ?? []}
                              setIsAddNewCard={setIsAddNewCard}
                              isAddNewCard={isAddNewCard}
                              handleApplePay={handleApplePay}
                              setAmtErr={setAmtErr}
                              setIsEdit={setIsEdit}
                              amtErr={amtErr}
                              isManualForm={isManualForm}
                              setIsManualForm={setIsManualForm}
                              savedCardsEmail={savedCardsEmail}
                              setInvoiceId={setInvoiceId}
                              setEmailDisplay={setEmail}
                            />
                          )
                        ) : paymentProcess ? (
                          <div className="w-full h-full grid place-items-center p-5">
                            {paymentProcess === "true" ? (
                              <PaymentScheduledSuccess
                                email={email}
                                amount={amount}
                                scheduleId={scheduleId}
                                sendEmail={sendEmail}
                                date={scheduleDate}
                                orderId={orderId}
                                showSuccessToast
                              />
                            ) : (
                              <PaymentScheduledFailed
                                failedReason={failedReason}
                                handleBackToStart={handleBackToStart}
                                amount={amount}
                                date={scheduleDate}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="lg:px-10 lg:pt-[90px] lg:max-h-full lg:h-full h-fit relative animate__animated animate__fadeIn">
                            <SchedulePayment
                              setIsGoogleLoading={setIsGoogleLoading}
                              generalMarkers={generalMarkers}
                              currentStep={currentStep}
                              completedSteps={completedSteps}
                              setIsStartDate={setIsStartDate}
                              data={data}
                              setData={setData}
                              isStartDate={isStartDate}
                              setIsApiCall={requestScheduleRefresh}
                              handleDebouncedChange={handleDebouncedChange}
                              handleSave={handleSave}
                              paymentPlanDataRedux={paymentPlanDataRedux}
                              dataPayment={dataPayment}
                              setDataPayment={setDataPayment}
                              addNewRow={addNewRow}
                              removeRow={removeRow}
                              handleChange={handleChange}
                              isLoading={isLoading}
                              accountLeadData={accountLeadData}
                              setCompletedSteps={setCompletedSteps}
                              setCurrentStep={setCurrentStep}
                              isDataLoading={isDataLoading}
                              makePayment={true}
                              handlePrev={handlePrev}
                              setSavedCardsEmail={setSavedCardsEmail}
                              setShowPreview={setShowPreview}
                              showPreview={showPreview}
                              setTab={setTab}
                              setPaymentProcess={setPaymentProcess}
                              handleGooglePay={handleGooglePay}
                              createPayPalIntent={createPayPalIntent}
                              isGoogleLoading={isGoogleLoading}
                              setIsAddNewCard={setIsAddNewCard}
                              isAddNewCard={isAddNewCard}
                              setIsConfirmButtonDisable={
                                setIsConfirmButtonDisable
                              }
                              isSelectedOffer={selectedOffer ? true : false}
                              handleApplePay={handleApplePay}
                              setHideButtons={setHideButtons}
                              amount={getAmount()}
                              handleSchedulePaymentCreate={
                                handleSchedulePaymentCreate
                              }
                              setIsSelectStartDate={setIsSelectStartDate}
                              setScheduleMethod={setScheduleMethod}
                              setFailedReason={setFailedReason}
                              setAmount={(val) => setAmount(String(val))}
                              sendEmail={sendEmail}
                              setScheduleDate={(val) =>
                                setScheduleDate(val as any)
                              }
                              setInvoiceId={setInvoiceId}
                              isEndDateError={
                                isEndDateError ||
                                Boolean(selectedOfferTransactionError)
                              }
                              dateErrMsg={
                                selectedOfferTransactionError?.message ||
                                dateErrMsg
                              }
                              setDateErrMsg={setDateErrMsg}
                              activeOffer={activeOffer}
                              vdcId={offerLinkage.vdc_id}
                              amountValidationError={activeScheduleAmountError}
                              amountLimitBalance={scheduleAmountLimitBalance}
                              onScheduleAmountChange={() =>
                                setScheduleAmountErr(null)
                              }
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {!hideButtons &&
                  !paymentProcess &&
                  currentStep > 1 &&
                  tab !== "one_time" &&
                  !isAssistanceRequested && (
                    <>
                      <div className="lg:absolute bg-white lg:bottom-0 -bottom-48 py-4 flex justify-between lg:px-8 px-5 z-20 border-t w-full gap-5">
                        <Button
                          className="lg:!w-40 !w-full"
                          isDisabled={isLoading}
                          text="Back"
                          type="button"
                          variant="secondary"
                          onClick={handlePrev}
                        />
                        <Button
                          className="lg:!w-40 !w-full"
                          isLoading={scheduleCreateLoading}
                          isDisabled={
                            isConfirmButtonDisable ||
                            isEndDateError ||
                            Boolean(selectedOfferTransactionError)
                          }
                          text="Confirm"
                          type="button"
                          onClick={() => {
                            handleSchedulePaymentCreate({});
                          }}
                          variant="variable"
                        />
                      </div>
                    </>
                  )}
              </>
            </div>
          )}
      </div>
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={showPreview}
        size={isLessThan590 ? `100vw` : "590px"}
        onClose={() => {
          setShowPreview(false);
          console.log("data?.paymentPlan 1=====", data?.paymentPlan);
        }}
        overlayColor="#000"
      >
        <PlanPreview
          show={showPreview}
          setShow={setShowPreview}
          schedule={data?.paymentPlan}
          isLoading={isLoading}
          scheduleMethod={scheduleMethod}
          isStax={isStax}
          paymentMethodToken={
            isAddNewCard
              ? scheduleMethod === "CC"
                ? dataPayment?.selectedCard?.cc_token
                : scheduleMethod === "ACH"
                  ? dataPayment?.selectedBank?.cc_token
                  : ""
              : ""
          }
        />
      </Drawer>

      <CustomModal
        signModal={false}
        isLoading={false}
        isOpen={showModal}
        title="Alert"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        doneText="Leave"
        cancleText="Stay"
        onDoneClick={() => {
          handleConfirmLeave();
        }}
        onCloseClick={() => {
          handleCancelLeave();
        }}
        children={
          <React.Fragment>
            <div className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
              Are you sure you want to leave the page? All progress will be
              lost.
            </div>
          </React.Fragment>
        }
      />
    </>
  );
};

export default MakePayment;
